import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { format, addDays, startOfWeek, subWeeks, addWeeks, formatDistanceToNow } from 'date-fns';
import { ChevronLeft, ChevronRight, Plus, Trash2, Copy, Save, Send, Sparkles, Wand2, History, AlertCircle, XCircle, RefreshCw, ChevronDown, ChevronUp, Info, RotateCcw, Lock, Lightbulb, Star, Calendar, Loader2, CheckCircle2, AlertTriangle, MessageSquare, Clock } from 'lucide-react';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ProjectSelector } from './ProjectSelector';
import { CostCentreSelector } from './CostCentreSelector';
import { RoleSelector } from './RoleSelector';
import { useTimesheetForScreen } from '@/contexts/TimesheetContext';
import { useDataSource } from '@/contexts/DataSourceContext';
import { useTour } from '@/contexts/useTour';
import { usePermissions } from '@/contexts/PermissionContext';
import { useSettings } from '@/contexts/SettingsContext';
import { useCompany } from '@/features/multi-company/context/CompanyContext';
import { useOvertimeTypes } from '@/hooks/useOvertimeTypes';
import type { TimesheetEntry, WorkingPattern } from '@/types/timesheet';
import { approvalsApi } from '@/services/approvalsApi';
import { timesheetApi, type PaginatedProjectsResponse, type PaginationInfo } from '@/services/timesheetApi';
import type { Project } from '@/types';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { aiService, isWarningBlocking } from '@/services/aiService';
import { rejectionService } from '@/services/rejectionService';
import { getOrganisationTimesheetSettings, getCachedLiveOrgSettings, getEmployeeWorkingPattern } from '@/services/settingsRepository';
import type { OrganisationTimesheetSettings } from '@/types/timesheetSettings';
import { DEFAULT_TIMESHEET_SETTINGS } from '@/types/timesheetSettings';
import { AnomalyAlertsPanelEnhanced } from './AIAssistant/AnomalyAlertsPanelEnhanced';
import { AIInsightsSheet } from './AIAssistant/AIInsightsSheet';
import { SmartAutoFillDialog } from './AIAssistant/SmartAutoFillDialog';
import { AuditHistoryDialog } from './AuditHistory';
import { PreStageRuleConfirmationDialog } from './PreStageRuleConfirmationDialog';
import { OvertimeTypeBadge } from './timesheet/OvertimeTypeBadge';
import { OvertimeClassificationModal } from './timesheet/OvertimeClassificationModal';
import type { OvertimeClassificationResult } from './timesheet/OvertimeClassificationModal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { OvertimeType } from '@/types/timesheetSettings';
import { BookingHoverCard, BookingActionMenu, ManualEntryActionMenu, BookingInlineActions, ManualEntryInlineActions, BookingDetailsModal, DismissBookingModal } from './ScheduledBookings';
import { AbsenceDetailsModal, AbsenceInlineActions } from './Absences';
import { absencesApi } from '@/services/absencesApi';
import { mapAbsenceToTimesheetEntry } from '@/utils/absenceMapper';
import { autoLoadScheduledBookings, autoLoadScheduledBookingsAsync, syncScheduledBookingsForWeek, syncScheduledBookingsAsync, hasEditedHours, dismissBooking, restoreBooking, type EmployeeRoleInfo } from '@/services/scheduledBookingService';
import { RecallTimesheetDialog } from './RecallTimesheetDialog';
import { recallTimesheet } from '@/services/timesheetApprovalService';
import { auditService } from '@/services/auditService';

import type { StatusDotType } from './ScheduledBookings/StatusDot';
import { AbsenceIndicator, ReadOnlyIndicator } from './AbsenceBadge';
import type { AbsenceType } from './AbsenceBadge';
import { PageHeader } from '@/components/blocks/page-header';
import { FeatureFeedbackCollector } from '@/components/FeedbackCollector';
import { getEffectiveDescriptionType } from '@/utils/descriptionTypeUtils';

const ALL_DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
const ALL_DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function buildDaysFromStart(
  weekStartDay: 0 | 1 | 2 | 3 | 4 | 5 | 6,
  workingDays?: Record<string, { isWorkingDay: boolean }>
) {
  const days: typeof ALL_DAYS[number][] = [];
  const labels: string[] = [];
  
  // Build all 7 days starting from weekStartDay
  for (let i = 0; i < 7; i++) {
    const idx = (weekStartDay + i) % 7;
    const day = ALL_DAYS[idx];
    
    // If workingDays config exists, only include working days
    // Otherwise, include all weekdays (exclude weekends by default)
    if (workingDays) {
      const isWorking = workingDays[day]?.isWorkingDay;
      if (isWorking) {
        days.push(day);
        labels.push(ALL_DAY_LABELS[idx]);
      } else {
      }
    } else if (day !== 'saturday' && day !== 'sunday') {
      days.push(day);
      labels.push(ALL_DAY_LABELS[idx]);
    }
  }
  
  return { days, labels };
}

/**
 * Build the set of days that are in the company's working pattern but NOT
 * in the employee's working pattern. These days should still appear in the
 * timesheet grid (so the Non-Working Day Entry policy check can fire) but
 * be visually marked as non-working.
 */
function getNonWorkingDaysForEmployee(
  companyWorkingDays: Record<string, { isWorkingDay: boolean }> | undefined,
  employeeWorkingPattern: WorkingPattern | undefined
): Set<typeof ALL_DAYS[number]> {
  const nonWorking = new Set<typeof ALL_DAYS[number]>();
  if (!companyWorkingDays || !employeeWorkingPattern?.workingDays) return nonWorking;

  for (const day of ALL_DAYS) {
    const companyWorking = companyWorkingDays[day]?.isWorkingDay ?? false;
    const employeeWorking = employeeWorkingPattern.workingDays[day] ?? false;
    if (companyWorking && !employeeWorking) {
      nonWorking.add(day);
    }
  }
  return nonWorking;
}

function isWeekendDay(day: typeof ALL_DAYS[number]): boolean {
  return day === 'saturday' || day === 'sunday';
}

// Navigation state types for drilldown from reports
type TimesheetDrilldownState = {
  employeeId: number;
  weekEnding: Date;
};

interface TimesheetGridProps {
  selection?: TimesheetDrilldownState;
}

// ─── OvertimeTypeCell ────────────────────────────────────────────────────────
// Extracted as a named sub-component to avoid complex nested ternaries in JSX
// that can cause Babel/Vite parse errors.

interface OvertimeTypeCellProps {
  entry: { overtimeTypeId?: number; overtimeTypeName?: string };
  overtimeTypes: OvertimeType[];
  isReadOnly: boolean;
  isAbsence: boolean;
  onChangeOvertimeType: (typeId: number | undefined) => void;
}

function OvertimeTypeCell({ entry, overtimeTypes, isReadOnly, isAbsence, onChangeOvertimeType }: OvertimeTypeCellProps) {
  if (isAbsence) {
    return <span className="text-muted-foreground text-xs">—</span>;
  }

  const selectedType = overtimeTypes.find(t => t.id === entry.overtimeTypeId);

  if (isReadOnly) {
    if (selectedType) {
      return <OvertimeTypeBadge name={selectedType.name} colour={selectedType.badgeColour} />;
    }
    return <span className="text-muted-foreground text-xs">—</span>;
  }

  return (
    <Select
      value={entry.overtimeTypeId != null ? String(entry.overtimeTypeId) : 'none'}
      onValueChange={(val) => onChangeOvertimeType(val === 'none' ? undefined : Number(val))}
    >
      <SelectTrigger className="h-9 text-xs min-w-[110px]">
        {selectedType ? (
          <div className="flex items-center gap-1.5 min-w-0">
            <span
              className="h-2.5 w-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: selectedType.badgeColour }}
            />
            <span className="truncate">{selectedType.name}</span>
          </div>
        ) : (
          <SelectValue placeholder="Standard" />
        )}
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none">
          <span className="text-muted-foreground">Standard</span>
        </SelectItem>
        {overtimeTypes.map(ot => (
          <SelectItem key={ot.id} value={String(ot.id)}>
            <div className="flex items-center gap-2">
              <span
                className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: ot.badgeColour }}
              />
              <span>{ot.name}</span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ────────────────────────────────────────────────────────────────────────────

const EMPTY_ENTRIES: TimesheetEntry[] = [];

export function TimesheetGrid({ selection }: TimesheetGridProps = {}) {
  const location = useLocation();
  const { 
    projects, 
    costCentres, 
    currentEmployee, 
    employees,
    timesheets, 
    updateTimesheet,
    updateCostCentreLastUsed,
    refreshEmployeeTimesheets
  } = useTimesheetForScreen('timesheet');
  const { getScreenMode } = useDataSource();
  const { startPendingTour } = useTour();
  const { canDeleteTimesheet, canDuplicateTimesheet, canRecallTimesheet, canCopyFromLastWeek, canUseSmartFill, canUseAiInsights } = usePermissions();
  const { weekStartDay } = useSettings();
  const { currentCompany } = useCompany();
  
  // Get current screen mode for workflow integration
  const screenMode = getScreenMode('timesheet');
  
  // Organization settings — loaded early so weekStartDay can drive day order
  const [organizationSettings, setOrganizationSettings] = useState<OrganisationTimesheetSettings | undefined>(() => {
    if (screenMode === 'live') {
      return getCachedLiveOrgSettings() || undefined;
    }
    return undefined;
  });

  // Fetch the employee's working pattern from the working patterns API
  // In LIVE mode, the /employees endpoint doesn't include workingPattern,
  // so we need to fetch it separately from /employees/{id}/working-patterns/current
  const [employeeWorkingPattern, setEmployeeWorkingPattern] = useState<WorkingPattern | undefined>(undefined);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await getOrganisationTimesheetSettings(screenMode);
        setOrganizationSettings(settings);
      } catch (error) {
        console.error('Failed to fetch organization settings:', error);
      }
    };
    fetchSettings();
  }, [screenMode]);

  useEffect(() => {
    const fetchEmployeePattern = async () => {
      try {
        const result = await getEmployeeWorkingPattern(currentEmployee.id, screenMode);
        setEmployeeWorkingPattern(result.pattern);
      } catch (error) {
        console.error('Failed to fetch employee working pattern:', error);
      }
    };
    fetchEmployeePattern();
  }, [currentEmployee.id, screenMode]);

  // Get navigation state from either props or location state
  const navigationState = selection || (location.state as { timesheetDrilldown?: TimesheetDrilldownState })?.timesheetDrilldown;
  
  // Determine which employee's timesheet to display
  // Employee.id is now a number type matching the backend INT type
  const targetEmployeeId = navigationState?.employeeId ?? currentEmployee.id;
  
  // Use weekStartDay from Settings context (which manages persistence)
  // Build days array based on weekStartDay and working days configuration
  // Always use the COMPANY (org) working pattern for grid visibility so that
  // days the company considers working always appear — even when the employee's
  // Resource Manager pattern marks them as non-working.  Those extra days are
  // styled as non-working and trigger the Non-Working Day Entry policy check.
  const companyWorkingDays = useMemo(() => {
    return organizationSettings?.workingProfile?.workingDays || DEFAULT_TIMESHEET_SETTINGS.workingProfile.workingDays;
  }, [organizationSettings?.workingProfile?.workingDays]);

  const { days: DAYS, labels: DAY_LABELS } = useMemo(() => {
    const result = buildDaysFromStart(weekStartDay, companyWorkingDays);
    return result;
  }, [weekStartDay, companyWorkingDays]);

  // Identify days that are working for the company but non-working for the
  // employee (different Resource Manager pattern).  These columns are shown
  // in the grid but styled distinctly so users know they are non-working.
  const nonWorkingDays = useMemo(() => {
    return getNonWorkingDaysForEmployee(companyWorkingDays, employeeWorkingPattern);
  }, [companyWorkingDays, employeeWorkingPattern]);

  // Initialize week based on selection or default to current week
  const [currentWeekStart, setCurrentWeekStart] = useState(() => 
    navigationState?.weekEnding
      ? startOfWeek(navigationState.weekEnding, { weekStartsOn: weekStartDay })
      : startOfWeek(new Date(), { weekStartsOn: weekStartDay })
  );

  // Check for pending tours when component mounts
  useEffect(() => {
    startPendingTour();
  }, [startPendingTour]);

  // Update current week when selection changes (e.g., navigating from Action Required card or Submission Consistency)
  useEffect(() => {
    if (navigationState?.weekEnding) {
      const newWeekStart = startOfWeek(navigationState.weekEnding, { weekStartsOn: weekStartDay });
      setCurrentWeekStart(newWeekStart);
    }
  }, [navigationState?.weekEnding]);

  // Recalculate current week start when weekStartDay setting changes
  useEffect(() => {
    setCurrentWeekStart(prev => {
      const newStart = startOfWeek(prev, { weekStartsOn: weekStartDay });
      // Only update if the week start actually changed to avoid unnecessary re-renders
      if (newStart.getTime() !== prev.getTime()) {
        return newStart;
      }
      return prev;
    });
  }, [weekStartDay]);

  // In live mode, refresh the employee's timesheets from the API to pick up
  // status changes that happened after the initial data load (e.g., a manager
  // rejected a timesheet while the employee was on another page).
  // This covers both direct navigation (e.g., sidebar click) and drilldown
  // navigation (e.g., from the Action Required card on the homepage).
  useEffect(() => {
    if (screenMode === 'live') {
      refreshEmployeeTimesheets(targetEmployeeId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigationState?.employeeId, navigationState?.weekEnding, screenMode]);

  // Get or create timesheet for current week
  // Filter by active company to ensure only the correct company's timesheet is shown.
  // Uses fuzzy week matching: a timesheet belongs to this week if its weekEnding falls
  // anywhere within the 7-day window [currentWeekStart, currentWeekStart + 6].
  // This prevents lookup failures when stored dates have been shifted by timezone drift.
  const currentTimesheet = useMemo(() => {
    const weekEnding = addDays(currentWeekStart, 6);
    const weekStartStr = format(currentWeekStart, 'yyyy-MM-dd');
    const weekEndStr = format(weekEnding, 'yyyy-MM-dd');
    
    const existing = timesheets.find(
      ts => ts.employeeId === targetEmployeeId && 
      format(ts.weekEnding, 'yyyy-MM-dd') >= weekStartStr &&
      format(ts.weekEnding, 'yyyy-MM-dd') <= weekEndStr &&
      (!currentCompany?.id || !ts.companyId || ts.companyId === currentCompany.id)
    );
    
    if (existing) {
      return existing;
    }
    
    // Fallback: only create new timesheets for the actual current user
    return {
      id: 0,
      employeeId: currentEmployee.id,
      employeeName: currentEmployee.name,
      weekEnding,
      status: 'draft' as const,
      entries: EMPTY_ENTRIES,
      companyId: currentCompany?.id,
    };
  }, [currentWeekStart, timesheets, targetEmployeeId, currentEmployee, currentCompany]);

    const [entries, setEntries] = useState<TimesheetEntry[]>(currentTimesheet.entries);
    const [showAutoFillDialog, setShowAutoFillDialog] = useState(false);
    const [showFillDescriptionDialog, setShowFillDescriptionDialog] = useState(false);
    const [bulkDescription, setBulkDescription] = useState('');
  const [showAuditHistory, setShowAuditHistory] = useState(false);
  const [showAIInsightsSheet, setShowAIInsightsSheet] = useState(false);
  const [dismissedWarnings, setDismissedWarnings] = useState<Set<string>>(new Set());
  
  // Scheduled bookings state
  const [selectedBookingForDetails, setSelectedBookingForDetails] = useState<TimesheetEntry | null>(null);
  const [selectedBookingForDismiss, setSelectedBookingForDismiss] = useState<TimesheetEntry | null>(null);
  const [hasLoadedScheduledBookings, setHasLoadedScheduledBookings] = useState(false);
  
  // Absence state
  const [selectedAbsenceForDetails, setSelectedAbsenceForDetails] = useState<TimesheetEntry | null>(null);
  const [hasLoadedAbsences, setHasLoadedAbsences] = useState(false);

  // Overtime classification state (task 2.17)
  const [classifyOvertimeEntry, setClassifyOvertimeEntry] = useState<TimesheetEntry | null>(null);
  /** Per-day OT suggestions pre-populated by the AI "Review overtime hours" CTA (task 3.11) */
  const [classifyOvertimeSuggestedHours, setClassifyOvertimeSuggestedHours] = useState<Partial<Record<import('@/types/timesheet').DayKey, number>>>({});

  // Track editing state for hours inputs to prevent premature autocomplete
  const [editingHours, setEditingHours] = useState<Record<string, string | undefined>>({});
  const [openNoteCell, setOpenNoteCell] = useState<string | null>(null); // `${entryId}-${day}`
  const [draftNote, setDraftNote] = useState<string>('');
  const getHoursCellKey = (entryId: number, day: typeof DAYS[number]) => `${entryId}-${day}`;

  // Track cost centres per project code for filtering
  // Key: project code, Value: array of cost centres for that project
  const [costCentresByProjectCode, setCostCentresByProjectCode] = useState<Record<string, typeof costCentres>>({});
  const [loadingCostCentresForProject, setLoadingCostCentresForProject] = useState<Record<string, boolean>>({});
  // Ref to track in-flight requests without causing re-renders/stale closures
  const loadingCostCentresRef = useRef<Record<string, boolean>>({});
  const fetchedCostCentresRef = useRef<Record<string, boolean>>({});

  // Paginated projects state for server-side pagination
  const PAGE_SIZE = 20;
  const [paginatedProjects, setPaginatedProjects] = useState<Project[]>([]);
  const [projectsPage, setProjectsPage] = useState(1);
  const [projectsTotalCount, setProjectsTotalCount] = useState(0);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsHasMore, setProjectsHasMore] = useState(true);
  const [projectsSearchQuery, setProjectsSearchQuery] = useState('');
  const [isSearchingProjects, setIsSearchingProjects] = useState(false);
  // Projects for existing entries that may not be in the paginated list
  const [existingEntryProjects, setExistingEntryProjects] = useState<Project[]>([]);
  // Projects from the recently-used list (localStorage) that may not be in the paginated list
  const [recentlyUsedProjects, setRecentlyUsedProjects] = useState<Project[]>([]);

  // Function to fetch cost centres filtered by project code
  const fetchCostCentresForProject = useCallback(async (projectCode: string) => {
    // Skip only if currently loading (prevent duplicate concurrent requests)
    if (loadingCostCentresRef.current[projectCode]) {
      return;
    }

    // Skip if already fetched (prevent redundant requests on effect re-runs)
    if (fetchedCostCentresRef.current[projectCode]) {
      return;
    }

    // In demo mode, use all cost centres (no filtering)
    if (screenMode === 'demo') {
      setCostCentresByProjectCode(prev => ({
        ...prev,
        [projectCode]: costCentres
      }));
      return;
    }

    // In live mode, fetch filtered cost centres from the API
    loadingCostCentresRef.current[projectCode] = true;
    setLoadingCostCentresForProject(prev => ({ ...prev, [projectCode]: true }));
    try {
      const filteredCostCentres = await timesheetApi.getCostCentresByProjectCode(projectCode);
      setCostCentresByProjectCode(prev => ({
        ...prev,
        [projectCode]: filteredCostCentres
      }));
      fetchedCostCentresRef.current[projectCode] = true;
    } catch (error) {
      console.error(`Failed to fetch cost centres for project ${projectCode}:`, error);
      // Fall back to all cost centres on error
      setCostCentresByProjectCode(prev => ({
        ...prev,
        [projectCode]: costCentres
      }));
    } finally {
      loadingCostCentresRef.current[projectCode] = false;
      setLoadingCostCentresForProject(prev => ({ ...prev, [projectCode]: false }));
    }
  }, [screenMode, costCentres]);

  // Load initial page of projects when component mounts (live mode only)
  const loadInitialProjects = React.useCallback(async () => {
    if (screenMode === 'demo') {
      // In demo mode, use all projects from context
      return;
    }
    
    setProjectsLoading(true);
    try {
      const response = await timesheetApi.getProjects({ 
        page: 1, 
        pageSize: PAGE_SIZE, 
        status: 'active',
        companyId: currentCompany?.id,
      });
      setPaginatedProjects(response.data);
      setProjectsTotalCount(response.pagination.totalItems);
      setProjectsHasMore(response.pagination.hasNextPage);
      setProjectsPage(1);
    } catch (error) {
      console.error('Failed to load projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setProjectsLoading(false);
    }
  }, [screenMode, currentCompany?.id]);

  // Load more projects (pagination)
  const loadMoreProjects = React.useCallback(async () => {
    if (projectsLoading || !projectsHasMore || screenMode === 'demo') return;
    
    setProjectsLoading(true);
    try {
      const nextPage = projectsPage + 1;
      const response = await timesheetApi.getProjects({ 
        page: nextPage, 
        pageSize: PAGE_SIZE, 
        status: 'active',
        search: projectsSearchQuery || undefined,
        companyId: currentCompany?.id,
      });
      setPaginatedProjects(prev => {
        const existingIds = new Set(prev.map(p => p.id));
        const newProjects = response.data.filter(p => !existingIds.has(p.id));
        return [...prev, ...newProjects];
      });
      setProjectsPage(nextPage);
      setProjectsHasMore(response.pagination.hasNextPage);
      setProjectsTotalCount(response.pagination.totalItems);
    } catch (error) {
      console.error('Failed to load more projects:', error);
      toast.error('Failed to load more projects');
    } finally {
      setProjectsLoading(false);
    }
  }, [projectsLoading, projectsHasMore, projectsPage, projectsSearchQuery, screenMode, currentCompany?.id]);

  // Handle server-side search for projects
  const handleProjectSearch = React.useCallback(async (query: string) => {
    if (screenMode === 'demo') return;
    
    setProjectsSearchQuery(query);
    setIsSearchingProjects(true);
    
    try {
      const response = await timesheetApi.getProjects({ 
        page: 1, 
        pageSize: PAGE_SIZE, 
        status: 'active',
        search: query || undefined,
        companyId: currentCompany?.id,
      });
      setPaginatedProjects(response.data);
      setProjectsTotalCount(response.pagination.totalItems);
      setProjectsHasMore(response.pagination.hasNextPage);
      setProjectsPage(1);
    } catch (error) {
      console.error('Failed to search projects:', error);
    } finally {
      setIsSearchingProjects(false);
    }
  }, [screenMode, currentCompany?.id]);

  // Fetch project details for existing entries that may not be in the paginated list
  const fetchExistingEntryProjects = React.useCallback(async () => {
    if (screenMode === 'demo') return;
    
    // Get unique project IDs from existing entries
    const existingProjectIds = new Set(
      entries
        .filter(e => e.projectId)
        .map(e => e.projectId)
    );
    
    // Find which project IDs are not in the paginated list
    const paginatedProjectIds = new Set(paginatedProjects.map(p => p.id));
    const missingProjectIds = Array.from(existingProjectIds).filter(
      id => id && !paginatedProjectIds.has(id)
    );
    
    if (missingProjectIds.length === 0) {
      setExistingEntryProjects([]);
      return;
    }
    
    // Fetch each missing project individually (Option C from the plan)
    const fetchedProjects: Project[] = [];
    for (const id of missingProjectIds) {
      if (id) {
        const project = await timesheetApi.getProjectById(id);
        if (project) {
          fetchedProjects.push(project);
        }
      }
    }
    setExistingEntryProjects(fetchedProjects);
  }, [entries, paginatedProjects, screenMode]);

  // Fetch recently used and favourited projects that may not be in the
  // paginated list.  The useProjectSelector hook stores these IDs in
  // localStorage but relies on the projects array it receives to build the
  // "Recently Used" and "Favourites" sections.  In live mode only the first
  // page of projects is loaded, so any saved project outside that page
  // silently disappears from the dropdown.  This mirrors the
  // fetchExistingEntryProjects pattern.
  const fetchRecentlyUsedProjects = React.useCallback(async () => {
    if (screenMode === 'demo') return;

    let savedIds: string[] = [];
    const storageKey = currentCompany?.id
      ? `project-selector-state-${currentCompany.id}`
      : 'project-selector-state';
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        const recentIds: string[] = (parsed.recentlyUsed || []).map((item: { id: string }) => item.id);
        const favouriteIds: string[] = parsed.favourites || [];
        const unique = new Set([...recentIds, ...favouriteIds]);
        savedIds = Array.from(unique);
      }
    } catch {
      return;
    }

    if (savedIds.length === 0) {
      setRecentlyUsedProjects([]);
      return;
    }

    const knownIds = new Set(paginatedProjects.map(p => p.id));
    const missingIds = savedIds.filter(id => id && !knownIds.has(id));

    if (missingIds.length === 0) {
      setRecentlyUsedProjects([]);
      return;
    }

    const fetched: Project[] = [];
    for (const id of missingIds) {
      try {
        const project = await timesheetApi.getProjectById(id);
        if (project) {
          fetched.push(project);
        }
      } catch {
        // Skip projects that can no longer be found
      }
    }
    setRecentlyUsedProjects(fetched);
  }, [paginatedProjects, screenMode, currentCompany?.id]);

  // Load initial projects on mount
  useEffect(() => {
    loadInitialProjects();
  }, [loadInitialProjects]);

  // Listen for company-changed event to reset local state and reload data.
  // Debounced to coalesce rapid switches and avoid stale-context 403s.
  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout>;

    const handleCompanyChange = (event: Event) => {
      clearTimeout(debounceTimer);
      const signal = (event as CustomEvent).detail?.signal as AbortSignal | undefined;

      debounceTimer = setTimeout(() => {
        if (signal?.aborted) return;

        // Cancel any pending auto-save so stale entries from the old company
        // are not written into the new company's timesheet.
        if (autoSaveTimerRef.current) {
          clearTimeout(autoSaveTimerRef.current);
          autoSaveTimerRef.current = null;
        }

        // Reset timesheet entries to prevent old company data from persisting
        setEntries(EMPTY_ENTRIES);

        // Reset paginated projects state
        setPaginatedProjects([]);
        setProjectsPage(1);
        setProjectsTotalCount(0);
        setProjectsHasMore(true);
        setProjectsSearchQuery('');
        setExistingEntryProjects([]);
        setRecentlyUsedProjects([]);

        // Reset cost centres cache
        setCostCentresByProjectCode({});
        fetchedCostCentresRef.current = {};

        // Reset scheduled bookings and absences so they are re-fetched for the new company
        setHasLoadedScheduledBookings(false);
        setHasLoadedAbsences(false);

        // Reload projects for the new company
        loadInitialProjects();
      }, 150);
    };

    window.addEventListener('company-changed', handleCompanyChange);
    return () => {
      clearTimeout(debounceTimer);
      window.removeEventListener('company-changed', handleCompanyChange);
    };
  }, [loadInitialProjects]);

  // Fetch existing entry projects when entries or paginated projects change
  useEffect(() => {
    fetchExistingEntryProjects();
  }, [fetchExistingEntryProjects]);

  // Fetch recently used projects when paginated projects change
  useEffect(() => {
    fetchRecentlyUsedProjects();
  }, [fetchRecentlyUsedProjects]);

  // Combine paginated projects with existing entry projects for the selector
  const allAvailableProjects = useMemo(() => {
    let result: Project[];
    if (screenMode === 'demo') {
      result = projects;
    } else {
      const seen = new Set<string>();
      const combined: Project[] = [];
      for (const p of paginatedProjects) {
        if (!seen.has(p.id)) {
          seen.add(p.id);
          combined.push(p);
        }
      }
      for (const p of existingEntryProjects) {
        if (!seen.has(p.id)) {
          seen.add(p.id);
          combined.push(p);
        }
      }
      for (const p of recentlyUsedProjects) {
        if (!seen.has(p.id)) {
          seen.add(p.id);
          combined.push(p);
        }
      }
      result = combined;
    }

    const showArchived = organizationSettings?.showArchivedProjects ?? false;
    if (!showArchived) {
      result = result.filter(p => p.active !== false || (p.status && p.status.toLowerCase() === 'active'));
    }

    const scope = organizationSettings?.projectSelectionScope ?? 'all';
    if (scope === 'assigned') {
      const usedProjectIds = new Set(entries.map(e => e.projectId).filter(Boolean));
      result = result.filter(p => p.isFavourite || usedProjectIds.has(p.id));
    }

    return result;
  }, [screenMode, projects, paginatedProjects, existingEntryProjects, recentlyUsedProjects, organizationSettings?.showArchivedProjects, organizationSettings?.projectSelectionScope, entries]);

  // Calculate remaining count for "Load More" button
  const projectsRemainingCount = useMemo(() => {
    return Math.max(0, projectsTotalCount - paginatedProjects.length);
  }, [projectsTotalCount, paginatedProjects.length]);

  // Fetch cost centres for existing entries when entries change
  // This ensures cost centres are available for entries that already have a project selected
  useEffect(() => {
    const projectCodes = new Set<string>();
    entries.forEach(entry => {
      if (entry.projectId) {
        // Use allAvailableProjects to find the project (includes paginated + existing entry projects)
        const project = allAvailableProjects.find(p => p.id === entry.projectId);
        if (project?.code) {
          projectCodes.add(project.code);
        }
      }
    });
    // Fetch cost centres for all unique project codes
    projectCodes.forEach(code => fetchCostCentresForProject(code));
  }, [entries, allAvailableProjects, fetchCostCentresForProject]);

  // Sync entries when timesheet changes(id or entries from context)
  // We need to sync when:
  // 1. The timesheet ID changes (navigating to different week)
  // 2. The entries in the context change (after submission, the backend returns updated timesheet)
  // 3. currentWeekStart changes — catches the case where both old and new week have id=0
  //    (empty timesheets) so neither id nor the stable EMPTY_ENTRIES reference changes,
  //    but we must still reset to the correct week's entries.
  React.useEffect(() => {
    setEntries(currentTimesheet.entries);
    fetchedCostCentresRef.current = {};
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWeekStart, currentTimesheet.id, currentTimesheet.entries]);

  // Reset the scheduled-bookings flag only when the week or timesheet identity
  // changes — NOT when entries change due to a mid-navigation save. This
  // prevents flushSync(updateTimesheet) inside navigateWeek from prematurely
  // resetting the flag before the week has actually changed.
  React.useEffect(() => {
    setHasLoadedScheduledBookings(false);
    setHasLoadedAbsences(false);
  }, [currentWeekStart, currentTimesheet.id]);

  // Monitor entries changes for debugging
  React.useEffect(() => {
    if (entries.length > 0) {
    }
  }, [entries]);

  // AI-powered anomaly detection
  // Uses organization settings from database (live mode) or localStorage (demo mode)
  // Use the fetched employee working pattern (from working patterns API),
  // falling back to the pattern on the employee object (from /employees API, if populated)
  const effectiveWorkingPattern = employeeWorkingPattern || currentEmployee?.workingPattern;

  // Truly raw warnings — before applyPolicyViolationSettings, used for blocking checks.
  // This ensures that e.g. an overtime-risk warning whose primary check type
  // (legal-weekly-maximum) is disabled is still visible to the blocking logic
  // which can fall back to the weekly-overage check.
  const rawAiWarnings = useMemo(() => {
    return aiService.detectAnomalies(
      entries, 
      currentWeekStart, 
      timesheets,
      effectiveWorkingPattern,
      organizationSettings,
      { skipPolicyFiltering: true }
    );
  }, [entries, currentWeekStart, timesheets, effectiveWorkingPattern, organizationSettings]);

  // Policy-filtered warnings for display (suppression, consolidation, severity overrides applied)
  const policyFilteredWarnings = useMemo(() => {
    return aiService.detectAnomalies(
      entries, 
      currentWeekStart, 
      timesheets,
      effectiveWorkingPattern,
      organizationSettings
    );
  }, [entries, currentWeekStart, timesheets, effectiveWorkingPattern, organizationSettings]);

  // Filtered warnings for display (dismissed ones removed)
  const aiWarnings = useMemo(() => {
    return policyFilteredWarnings.filter(w => !dismissedWarnings.has(w.id));
  }, [policyFilteredWarnings, dismissedWarnings]);

  // Calculate AI Insights badge counts
  const aiInsightsCounts = useMemo(() => {
    const counts = { high: 0, medium: 0, low: 0 };
    aiWarnings.forEach(warning => {
      if (warning.severity === 'high') counts.high++;
      else if (warning.severity === 'medium') counts.medium++;
      else if (warning.severity === 'low') counts.low++;
    });
    return counts;
  }, [aiWarnings]);

  // Blocking check uses RAW (unfiltered) warnings so dismissals cannot bypass it
  const hasBlockingPolicyViolation = useMemo(() => {
    const checks = organizationSettings?.policyViolations?.checks;
    if (!checks || rawAiWarnings.length === 0) return false;
    const monitoringFrequency = organizationSettings?.policyViolations?.monitoringFrequency ?? 'week';
    return rawAiWarnings.some(warning => isWarningBlocking(warning, checks, monitoringFrequency));
  }, [rawAiWarnings, organizationSettings?.policyViolations?.checks, organizationSettings?.policyViolations?.monitoringFrequency]);

  // Set of warning IDs that correspond to blocking policy checks (non-dismissable).
  // Uses policyFilteredWarnings (the displayed set) so IDs match the UI.
  const nonDismissableWarningIds = useMemo(() => {
    const checks = organizationSettings?.policyViolations?.checks;
    if (!checks) return new Set<string>();
    const monitoringFrequency = organizationSettings?.policyViolations?.monitoringFrequency ?? 'week';
    const ids = new Set<string>();
    policyFilteredWarnings.forEach(w => {
      if (isWarningBlocking(w, checks, monitoringFrequency)) ids.add(w.id);
    });
    return ids;
  }, [policyFilteredWarnings, organizationSettings?.policyViolations?.checks, organizationSettings?.policyViolations?.monitoringFrequency]);

  // Two-tier gate: org setting (master switch) AND role permission must both be true.
  // Org OFF → hidden for everyone. Org ON → role permission decides per user.
  const enableCopyPreviousWeek = (organizationSettings?.deadline?.enableCopyPreviousWeek ?? true) && canCopyFromLastWeek();
  const enableAutoSmartFill = (organizationSettings?.deadline?.enableAutoSmartFill ?? true) && canUseSmartFill();
  const enableAiInsights = (organizationSettings?.deadline?.enableAiInsights ?? true) && canUseAiInsights();

    // AI-powered auto-fill suggestions
    const autoFillSuggestions = useMemo(() => {
      return aiService.generateAutoFillSuggestions(
        targetEmployeeId,
        timesheets,
        currentWeekStart
      );
    }, [targetEmployeeId, timesheets, currentWeekStart]);

  // DAYS already contains only working days in the correct order from buildDaysFromStart
  const VISIBLE_DAYS = DAYS;

  // Calculate totals
  const rowTotals = useMemo(() => {
    return entries.map(entry => 
      VISIBLE_DAYS.reduce((sum, day) => sum + (entry.hours[day] || 0), 0)
    );
  }, [entries, VISIBLE_DAYS]);

  const columnTotals = useMemo(() => {
    return VISIBLE_DAYS.map(day => 
      entries.reduce((sum, entry) => sum + (entry.hours[day] || 0), 0)
    );
  }, [entries, VISIBLE_DAYS]);

  const weekTotal = useMemo(() => 
    columnTotals.reduce((sum, total) => sum + total, 0)
  , [columnTotals]);

  // ── Overtime derived state ────────────────────────────────────────────────
  const overtimeEnabled = organizationSettings?.overtime?.enabled === true;

  // Fetch overtime types from the OvertimeTypes API instead of organizationSettings
  const { activeTypes: apiOvertimeTypes } = useOvertimeTypes({ enabled: overtimeEnabled, screenId: 'timesheet' });

  const activeOvertimeTypes = useMemo<OvertimeType[]>(() => {
    if (!overtimeEnabled) return [];
    return apiOvertimeTypes;
  }, [overtimeEnabled, apiOvertimeTypes]);

  /** ID of the org-default active work type — used to pre-populate overtime CTAs. */
  const defaultOvertimeTypeId = useMemo<number | undefined>(() => {
    return activeOvertimeTypes.find(t => t.isDefault)?.id;
  }, [activeOvertimeTypes]);

  /**
   * Computes daily and/or weekly threshold values.
   * Both are independently optional — only levels that are genuinely configured
   * will produce amber highlights; no phantom fallback defaults.
   */
  const overtimeThresholds = useMemo<{ daily?: number; weekly?: number } | null>(() => {
    if (!overtimeEnabled) return null;
    const thresholds = organizationSettings?.overtime?.thresholds;
    if (!thresholds || thresholds.mode === 'manual-only') return null;

    // Prefer employee working pattern over org profile
    const weeklyHours = employeeWorkingPattern?.hoursPerWeek
      || organizationSettings?.workingProfile?.standardHoursPerWeek;
    if (!weeklyHours) return null;

    // Count working days from employee pattern or org profile
    let workingDayCount: number;
    if (employeeWorkingPattern?.workingDays) {
      workingDayCount = Object.values(employeeWorkingPattern.workingDays).filter(Boolean).length;
    } else {
      const profile = organizationSettings?.workingProfile;
      workingDayCount = profile ? Object.values(profile.workingDays).filter(d => d.isWorkingDay).length : 5;
    }

    const daily = workingDayCount > 0 ? weeklyHours / workingDayCount : undefined;
    return { daily, weekly: weeklyHours };
  }, [overtimeEnabled, organizationSettings?.overtime?.thresholds, organizationSettings?.workingProfile, employeeWorkingPattern]);

  /**
   * Whether thresholdScope is 'total-hours'.
   * When true, all hours (classified + unclassified) count against the threshold.
   * When false/undefined (default 'unclassified-only'), only unclassified rows count.
   */
  const thresholdScopeTotalHours =
    organizationSettings?.overtime?.thresholds?.thresholdScope === 'total-hours';

  /**
   * Daily totals used for threshold comparison.
   * - 'unclassified-only' (default): only rows without a work type count — the highlight
   *   is a prompt to classify, not a permanent warning on already-handled rows.
   * - 'total-hours': all hours count regardless of classification.
   * Either way, only UNCLASSIFIED rows are ever highlighted.
   */
  const unclassifiedColumnTotals = useMemo(() => {
    return VISIBLE_DAYS.map(day =>
      entries
        .filter(entry => thresholdScopeTotalHours ? true : !entry.overtimeTypeId)
        .reduce((sum, entry) => sum + (entry.hours[day] || 0), 0)
    );
  }, [entries, VISIBLE_DAYS, thresholdScopeTotalHours]);

  /**
   * Day-level highlighting respects the existing 'daily-overage' policy check.
   * If the admin has disabled daily-overage in Policy Checks (General tab), no day-level
   * highlights fire — regardless of what the work type threshold mode is set to.
   */
  const dailyPolicyCheckEnabled =
    organizationSettings?.policyViolations?.checks?.['daily-overage']?.enabled ?? true;

  /**
   * Week-level highlighting respects the existing 'weekly-overage' policy check.
   */
  const weeklyPolicyCheckEnabled =
    organizationSettings?.policyViolations?.checks?.['weekly-overage']?.enabled ?? true;

  /** Set of day keys (e.g. 'monday') where hours exceed the daily threshold */
  const daysExceedingThreshold = useMemo<Set<string>>(() => {
    if (!dailyPolicyCheckEnabled) return new Set();
    if (!overtimeThresholds?.daily) return new Set();
    const result = new Set<string>();
    VISIBLE_DAYS.forEach((day, i) => {
      if (unclassifiedColumnTotals[i] > overtimeThresholds.daily!) result.add(day);
    });
    return result;
  }, [overtimeThresholds, unclassifiedColumnTotals, VISIBLE_DAYS, dailyPolicyCheckEnabled]);

  const unclassifiedWeekTotal = useMemo(
    () => unclassifiedColumnTotals.reduce((sum, t) => sum + t, 0),
    [unclassifiedColumnTotals]
  );
  const weekExceedsThreshold =
    weeklyPolicyCheckEnabled &&
    overtimeThresholds?.weekly != null &&
    unclassifiedWeekTotal > overtimeThresholds.weekly;
  // ─────────────────────────────────────────────────────────────────────────
  const isDaily = getEffectiveDescriptionType(
    organizationSettings?.descriptionType ?? 'row',
    currentTimesheet.status,
    currentTimesheet.entries,
  ) === 'daily';

    // Check for missing descriptions (used for submission validation)
    // Only consider descriptions missing if they are required by organization settings
    const hasMissingDescriptions = useMemo(() => {
      if (organizationSettings?.requireDescription !== 'always') {
        return false; // Descriptions not required, so never consider them missing
      }
      if (isDaily) {
        // Daily mode — every day with hours > 0 must have a note
        return entries.some(entry =>
          !entry.isReadOnly &&
          VISIBLE_DAYS.some(day => (entry.hours[day] ?? 0) > 0 && !entry.dailyNotes?.[day]?.trim())
        );
      }
      // Row mode — every entry must have a description
      return entries.some(entry => !entry.isReadOnly && !entry.description.trim());
    }, [entries, organizationSettings?.requireDescription, isDaily, VISIBLE_DAYS]);

    // Check for missing projects (always required — a timesheet row without a project is meaningless)
    const hasMissingProjects = useMemo(() => {
      return entries.some(entry => !entry.isReadOnly && !entry.projectId);
    }, [entries]);

    // Check for missing roles (used for submission validation)
    // Only consider roles missing if showRole AND requireRole are both enabled
    const hasMissingRoles = useMemo(() => {
      if (!organizationSettings?.showRole || !organizationSettings?.requireRole) {
        return false;
      }
      return entries.some(entry => !entry.roleId);
    }, [entries, organizationSettings?.showRole, organizationSettings?.requireRole]);

    // Count entries with empty descriptions for bulk fill feature
    const emptyDescriptionCount = useMemo(() => {
      if (isDaily) return 0;
      return entries.filter(entry => !entry.description.trim()).length;
    }, [entries, isDaily]);

    // Handler for filling empty descriptions in bulk
    const handleFillEmptyDescriptions = () => {
      if (!bulkDescription.trim()) {
        toast.error('Please enter a description');
        return;
      }
    
      const updatedEntries = entries.map(entry => {
        if (!entry.description.trim()) {
          return { ...entry, description: bulkDescription.trim() };
        }
        return entry;
      });
    
      setEntries(updatedEntries);
      setShowFillDescriptionDialog(false);
      setBulkDescription('');
    };

    const addRow = () => {
    const newEntry: TimesheetEntry = {
      id: -Date.now(),
      projectId: '',
      costCentreId: '',
      roleId: currentEmployee?.roleId, // Default from employee's role, can be changed
      roleName: currentEmployee?.roleId ? currentEmployee?.role : undefined, // Denormalized role name for display
      description: '',
      hours: {
        monday: 0,
        tuesday: 0,
        wednesday: 0,
        thursday: 0,
        friday: 0,
        saturday: 0,
        sunday: 0
      },
    };
    setEntries(prevEntries => [...prevEntries, newEntry]);
  };

  // Scroll table to bottom when a new row is added
  const prevEntriesLengthRef = useRef<number>(0);
  useEffect(() => {
    if (entries.length > prevEntriesLengthRef.current && tableScrollRef.current) {
      tableScrollRef.current.scrollTop = tableScrollRef.current.scrollHeight;
    }
    prevEntriesLengthRef.current = entries.length;
  }, [entries.length]);

  // State for delete confirmation dialog
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ open: boolean; index: number | null }>({
    open: false,
    index: null,
  });

  const deleteRow= (index: number) => {
    // Show confirmation dialog
    setDeleteConfirmation({ open: true, index });
  };

  const confirmDelete = () => {
    if (!canDeleteTimesheet()) {
      toast.error('You do not have permission to delete timesheet lines');
      setDeleteConfirmation({ open: false, index: null });
      return;
    }
    if (deleteConfirmation.index !== null) {
      setEntries(prevEntries => prevEntries.filter((_, i) => i !== deleteConfirmation.index));
      setDeleteConfirmation({ open: false, index: null });
    }
  };

  const duplicateRow = (index: number) => {
    const entry = entries[index];
    const newEntry: TimesheetEntry = {
      ...entry,
      id: -Date.now(),
      hours: {
        monday: 0,
        tuesday: 0,
        wednesday: 0,
        thursday: 0,
        friday: 0,
        saturday: 0,
        sunday: 0
      },
      // Remove scheduled booking flags for duplicated entries
      isFromSchedule: false,
      scheduledBookingId: undefined,
    };
    setEntries(prevEntries => [...prevEntries.slice(0, index + 1), newEntry, ...prevEntries.slice(index + 1)]);
    
    // Focus on the new row after a short delay to allow DOM to update
    setTimeout(() => {
      // Find the first input in the new row (project selector)
      const newRowIndex = index + 1;
      const projectInput = document.querySelector(`[data-row-index="${newRowIndex}"] input`);
      if (projectInput instanceof HTMLElement) {
        projectInput.focus();
      }
    }, 100);
  };



  /**
   * Task 3.11 — Called by AIInsightsSheet when the user clicks "Review overtime hours"
   * on a `contracted-hours-exceeded` warning. Finds the largest non-OT manual row
   * by total hours and opens the OvertimeClassificationModal pre-populated with
   * the per-day suggestions from the AI warning.
   */
  const handleAIClassifyOvertime = (suggestedOvertimeHours: Partial<Record<string, number>>) => {
    // Only act when overtime is enabled
    if (!overtimeEnabled) return;

    // Find the best candidate row: largest total hours among non-OT manual (non-scheduled, non-absence) entries
    const candidateEntry = entries
      .filter((e) => !e.isFromSchedule && !e.absenceType && !e.overtimeTypeId)
      .sort((a, b) => {
        const totalA = Object.values(a.hours).reduce((s, h) => s + h, 0);
        const totalB = Object.values(b.hours).reduce((s, h) => s + h, 0);
        return totalB - totalA;
      })[0] ?? null;

    if (!candidateEntry) return;

    // Store suggested hours so the modal can pre-populate them
    setClassifyOvertimeSuggestedHours(suggestedOvertimeHours as Partial<Record<import('@/types/timesheet').DayKey, number>>);
    setClassifyOvertimeEntry(candidateEntry);
  };

  /**
   * Called by OvertimeClassificationModal on confirm (task 2.17).
   * Replaces the original entry with the standard-hours version and inserts
   * the new OT row immediately after it.
   */
  const handleClassifyOvertimeConfirm = (result: OvertimeClassificationResult) => {
    setEntries((prev) => {
      const idx = prev.findIndex((e) => e.id === result.updatedEntry.id);
      if (idx === -1) {
        // Fallback: just append the new OT row
        return [...prev, result.newOvertimeEntry];
      }
      return [
        ...prev.slice(0, idx),
        result.updatedEntry,
        result.newOvertimeEntry,
        ...prev.slice(idx + 1),
      ];
    });
    setClassifyOvertimeEntry(null);
    toast.success('Overtime classified — new row added below');
  };

  const handleFlagOvertimeRows = () => {
    const defaultOvertimeType =
      activeOvertimeTypes.find((t) => t.isDefault) ?? activeOvertimeTypes[0];

    if (!defaultOvertimeType) {
      toast.error('No overtime type configured. Add one in Settings → Overtime.');
      return;
    }

    let flaggedCount = 0;

    setEntries((prevEntries) => {
      return prevEntries.map((entry) => {
        // Skip rows already assigned an overtime type
        if (entry.overtimeTypeId) return entry;

        // Determine whether this entry has hours on a day that exceeds the threshold
        const hasHoursOnExceedingDay = weekExceedsThreshold
          ? Object.values(entry.hours).some((h) => (h ?? 0) > 0)
          : VISIBLE_DAYS.some(
              (day) => daysExceedingThreshold.has(day) && (entry.hours[day] ?? 0) > 0
            );

        if (!hasHoursOnExceedingDay) return entry;

        flaggedCount++;
        return {
          ...entry,
          overtimeTypeId: defaultOvertimeType.id,
          overtimeTypeName: defaultOvertimeType.name,
          overtimeCostMultiplier: defaultOvertimeType.costMultiplier,
          overtimeBillMultiplier: defaultOvertimeType.billMultiplier,
        };
      });
    });

  };

  const updateEntry = (index: number, field: keyof TimesheetEntry, value: any) => {
    
    setEntries(prevEntries => {
      if (index < 0 || index >= prevEntries.length) {
        console.error('❌ Invalid index:', index, 'Total entries:', prevEntries.length);
        toast.error('Error updating entry: Invalid row index');
        return prevEntries;
      }
      
      const updated = [...prevEntries];
      updated[index] = { ...updated[index], [field]: value };
      
      return updated;
    });
  };

  const updateHours = (index: number, day: typeof DAYS[number], value: string) => {
    const hours = value === '' ? 0 : parseFloat(value);
    const final = isNaN(hours) ? 0 : hours;
    setEntries(prevEntries => {
      const updated = [...prevEntries];
      updated[index] = {
        ...updated[index],
        hours: { ...updated[index].hours, [day]: final }
      };
      return updated;
    });
  };

  const copyFromLastWeek = () => {
    const lastWeek = subWeeks(currentWeekStart, 1);
    const lastWeekEnding = addDays(lastWeek, 6);
    const lastTimesheet = timesheets.find(
      ts => ts.employeeId === targetEmployeeId && 
      format(ts.weekEnding, 'yyyy-MM-dd') === format(lastWeekEnding, 'yyyy-MM-dd')
    );

    if (lastTimesheet && lastTimesheet.entries.length > 0) {
      let timesheetNativeEntries = lastTimesheet.entries.filter(entry => {
        if (entry.isFromSchedule) return false;
        if (entry.sourceIntegration) return false;
        if (entry.absenceType) return false;
        return true;
      });

      const shouldCopyOnlyBillable = organizationSettings?.deadline?.copyOnlyBillable ?? false;
      if (shouldCopyOnlyBillable) {
        timesheetNativeEntries = timesheetNativeEntries.filter(entry => {
          const proj = allAvailableProjects.find(p => p.id === entry.projectId);
          return proj?.billable;
        });
      }

      const shouldCopyDescriptions = organizationSettings?.deadline?.copyDescriptions ?? true;

      if (timesheetNativeEntries.length > 0) {
        setEntries(timesheetNativeEntries.map((entry, index) => ({
          ...entry,
          id: -Date.now() - index,
          description: shouldCopyDescriptions ? entry.description : '',
          dailyNotes: isDaily ? (shouldCopyDescriptions ? { ...entry.dailyNotes } : {}) : entry.dailyNotes,
          hours: {
            monday: 0,
            tuesday: 0,
            wednesday: 0,
            thursday: 0,
            friday: 0,
            saturday: 0,
            sunday: 0
          }
        })));
      } else {
        toast.error('No timesheet entries found to copy (absences and scheduled bookings are excluded)');
      }
    } else {
      toast.error('No timesheet found for last week');
    }
  };

  const saveTimesheet = async () => {
    if (isSaving || isSubmitting) return;

    // Validate role requirement on save
    if (organizationSettings?.showRole && organizationSettings?.requireRole) {
      const missingRoles = entries.filter(entry => !entry.roleId);
      if (missingRoles.length > 0) {
        toast.error('Cannot save timesheet', {
          description: 'All entries must have a role assigned. Please add roles to all entries.',
          duration: 5000,
        });
        return;
      }
    }

    // Cancel any pending auto-save timer — manual save takes over
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);

    setIsSaving(true);
    try {
      const updated = {
        ...currentTimesheet,
        entries: entriesRef.current,  // use ref so we always save the very latest entries
        status: 'draft' as const
      };
      updateTimesheet(updated);
      setLastSaved(new Date());
    } finally {
      setIsSaving(false);
    }
  };

  const submitTimesheet = async () => {
    if (isSaving || isSubmitting) return;

    if (hasBlockingPolicyViolation) {
      toast.error('Submission blocked by policy', {
        description: 'A policy violation is preventing submission. Please review AI Insights for details.',
        duration: 5000,
      });
      return;
    }

    if (entries.length === 0) {
      toast.error('Cannot submit empty timesheet');
      return;
    }

        if (organizationSettings?.requireDescription === 'always' && hasMissingDescriptions) {
          toast.error(
            isDaily
              ? 'Please add daily descriptions to all entries before submitting'
              : 'Please add descriptions to all entries'
          );
          return;
        }

    // Validate project and cost centre requirements
    const settings = screenMode === 'live' 
      ? getCachedLiveOrgSettings() 
      : await getOrganisationTimesheetSettings(screenMode);
    
    const missingProjects = entries.filter(entry => !entry.projectId);
    if (missingProjects.length > 0) {
      toast.error('Cannot submit timesheet', {
        description: 'All entries must have a project assigned. Please add projects to all entries.',
        duration: 5000,
      });
      return;
    }

    // Only validate cost centre if it's required in settings
    if (settings?.requireCostCentre) {
      const missingCostCentres = entries.filter(entry => !entry.costCentreId);
      if (missingCostCentres.length > 0) {
        toast.error('Cannot submit timesheet', {
          description: 'All entries must have a cost centre assigned. Please add cost centres to all entries.',
          duration: 5000,
        });
        return;
      }
    }

    // Only validate role if it's shown and required in settings
    if (settings?.showRole && settings?.requireRole) {
      const missingRoles = entries.filter(entry => !entry.roleId);
      if (missingRoles.length > 0) {
        toast.error('Cannot submit timesheet', {
          description: 'All entries must have a role assigned. Please add roles to all entries.',
          duration: 5000,
        });
        return;
      }
    }

    // Check minimum hours requirement based on employee's working pattern
    const expectedWeeklyHours = effectiveWorkingPattern?.hoursPerWeek || 40;
    const totalHours = entries.reduce((sum, entry) => {
      return sum + Object.values(entry.hours).reduce((h, v) => h + v, 0);
    }, 0);
    
    const minimumHoursThreshold = expectedWeeklyHours * 0.8; // Allow 20% tolerance
    
    if (totalHours < minimumHoursThreshold) {
      toast.error(
        `Insufficient hours logged`,
        {
          description: `You have logged ${totalHours.toFixed(1)}h but need at least ${minimumHoursThreshold.toFixed(1)}h (${expectedWeeklyHours}h expected). Please add missing time entries.`,
          duration: 6000,
        }
      );
      return;
    }

    // Show loading toast - declared outside try block so it can be dismissed in catch
    const loadingToast = toast.loading('Submitting timesheet...');
    setIsSubmitting(true);

    try {

      if (screenMode === 'live') {
        // LIVE MODE: Use backend API to trigger workflow engine
        
        // Prepare timesheet with entries
        const timesheetToSubmit = {
          ...currentTimesheet,
          entries,
        };
        
        // Check if this is a new timesheet that needs to be created first
        const isNewTimesheet = !currentTimesheet.id || currentTimesheet.id === 0;
        let timesheetId = currentTimesheet.id;
        
        if (isNewTimesheet) {
          const createdTimesheet = await timesheetApi.createTimesheet({
            ...timesheetToSubmit,
            id: 0,
            status: 'draft',
          });
          timesheetId = createdTimesheet.id;
        } else {
          // Update existing timesheet with current entries before submitting
          await timesheetApi.updateTimesheet(timesheetId, {
            ...timesheetToSubmit,
            status: 'draft', // Keep as draft, submit endpoint will change status
          });
        }
        
        // Now call the submit endpoint to trigger the workflow engine
        const submitResponse = await approvalsApi.submitTimesheet(timesheetId);
        
        // Dismiss loading toast
        toast.dismiss(loadingToast);

        // If the backend signals that a pre-stage rule requires user confirmation,
        // show the Accept/Decline dialog and halt the submission flow.
        if (submitResponse.requiresConfirmation && submitResponse.confirmationMessage) {
          setPendingConfirmTimesheetId(timesheetId);
          setConfirmationMessage(submitResponse.confirmationMessage);
          setConfirmationDialogOpen(true);
          setIsSubmitting(false);
          return;
        }
        
        if (submitResponse.message) {
          toast.success(submitResponse.message, {
            description: submitResponse.workflowInstanceId 
              ? 'Workflow started successfully' 
              : undefined,
            duration: 4000,
          });
        } else {
          toast.success('Timesheet submitted for approval');
        }
        
        // Fetch the updated timesheet from the server to get the latest state
        const updatedTimesheet = await timesheetApi.getTimesheetById(timesheetId);
        if (updatedTimesheet) {
          updateTimesheet(updatedTimesheet);
        }

        // Notify dashboard to refresh submission consistency stats
        window.dispatchEvent(new Event('timesheet-submitted'));
        
      } else {
        // DEMO MODE: Use client-side workflow simulation (existing behavior)
        
        // Import the submission service dynamically
        const { submitTimesheetWithWorkflow } = await import('@/services/timesheetSubmissionService');

        // Prepare timesheet with entries
        const timesheetToSubmit = {
          ...currentTimesheet,
          entries,
        };

        // Submit with full workflow integration (client-side)
        const result = await submitTimesheetWithWorkflow(
          timesheetToSubmit,
          currentEmployee,
          employees,
          timesheets
        );

        // Dismiss loading toast
        toast.dismiss(loadingToast);

        if (result.success) {
          // Update the timesheet in context
          updateTimesheet(result.timesheet);

          // Show success message with details
          if (result.autoApproved) {
            toast.success(result.message, {
              description: result.workflowName ? `Via ${result.workflowName}` : undefined,
              duration: 5000,
            });
          } else {
            toast.success(result.message, {
              description: result.workflowName ? `Via ${result.workflowName}` : undefined,
              duration: 4000,
            });
          }

        } else {
          toast.error('Failed to submit timesheet');
        }
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error('Error submitting timesheet:', error);
      toast.error('Failed to submit timesheet. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handlers for the pre-stage rule confirmation dialog (Accept / Decline)
  const handleConfirmationAccept = async () => {
    if (pendingConfirmTimesheetId == null) return;
    setIsSubmitting(true);
    const loadingToast = toast.loading('Submitting timesheet...');
    try {
      const response = await approvalsApi.confirmSubmission(pendingConfirmTimesheetId, true);
      toast.dismiss(loadingToast);

      if (response.message) {
        toast.success(response.message, { duration: 4000 });
      } else {
        toast.success('Timesheet submitted for approval');
      }

      const updatedTimesheet = await timesheetApi.getTimesheetById(pendingConfirmTimesheetId);
      if (updatedTimesheet) {
        updateTimesheet(updatedTimesheet);
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error('Error confirming submission:', error);
      toast.error('Failed to submit timesheet. Please try again.');
    } finally {
      setIsSubmitting(false);
      setConfirmationDialogOpen(false);
      setPendingConfirmTimesheetId(null);
      setConfirmationMessage('');
    }
  };

  const handleConfirmationDecline = async () => {
    if (pendingConfirmTimesheetId == null) return;
    try {
      await approvalsApi.confirmSubmission(pendingConfirmTimesheetId, false);
      toast.info('Submission cancelled');
    } catch (error) {
      console.error('Error declining submission:', error);
    } finally {
      setConfirmationDialogOpen(false);
      setPendingConfirmTimesheetId(null);
      setConfirmationMessage('');
    }
  };

  const canNavigatePrev = useMemo(() => {
    const limit = organizationSettings?.historicalEntryLimit ?? 4;
    const earliest = subWeeks(startOfWeek(new Date(), { weekStartsOn: weekStartDay }), limit);
    return subWeeks(currentWeekStart, 1) >= earliest;
  }, [currentWeekStart, organizationSettings?.historicalEntryLimit, weekStartDay]);

  const canNavigateNext = useMemo(() => {
    const setting = organizationSettings?.allowFutureDatedEntries ?? '2weeks';
    if (setting === 'unlimited') return true;
    const now = startOfWeek(new Date(), { weekStartsOn: weekStartDay });
    if (setting === 'no') return addWeeks(currentWeekStart, 1) <= now;
    const weeksAhead = setting === '1week' ? 1 : setting === '2weeks' ? 2 : 4;
    return addWeeks(currentWeekStart, 1) <= addWeeks(now, weeksAhead);
  }, [currentWeekStart, organizationSettings?.allowFutureDatedEntries, weekStartDay]);

  const minAllowedDate = useMemo(() => {
    const limit = organizationSettings?.historicalEntryLimit ?? 4;
    return subWeeks(startOfWeek(new Date(), { weekStartsOn: weekStartDay }), limit);
  }, [organizationSettings?.historicalEntryLimit, weekStartDay]);

  const maxAllowedDate = useMemo(() => {
    const setting = organizationSettings?.allowFutureDatedEntries ?? '2weeks';
    const now = startOfWeek(new Date(), { weekStartsOn: weekStartDay });
    if (setting === 'unlimited') return undefined;
    if (setting === 'no') return addDays(now, 6);
    const weeksAhead = setting === '1week' ? 1 : setting === '2weeks' ? 2 : 4;
    return addDays(addWeeks(now, weeksAhead), 6);
  }, [organizationSettings?.allowFutureDatedEntries, weekStartDay]);

  const navigateWeek = (direction: 'prev' | 'next') => {
    if (direction === 'prev' && !canNavigatePrev) return;
    if (direction === 'next' && !canNavigateNext) return;

    // Cancel any pending auto-save timer before we navigate (it would write to the wrong week)
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);

    // Save immediately if there are unsaved changes before switching week.
    // Use entriesRef.current (not the closed-over `entries`) so we always get
    // the very latest state — even if the user selected a project/cost centre
    // and clicked the arrow in the same event-loop tick.
    const latestEntries = entriesRef.current;
    const dirtyNow = !isReadOnly && (
      latestEntries.length !== currentTimesheet.entries.length ||
      JSON.stringify(latestEntries) !== JSON.stringify(currentTimesheet.entries)
    );
    if (dirtyNow && !isSaving && !isSubmitting) {
      const updated = { ...currentTimesheet, entries: latestEntries, status: 'draft' as const };
      // flushSync forces React to commit the context state update (timesheets array)
      // BEFORE setCurrentWeekStart runs. Without this, React 18 batches both state
      // updates into one render — currentTimesheet recomputes for the NEW week before
      // the save lands, causing the sync effect to wipe local entries to [].
      flushSync(() => {
        updateTimesheet(updated);
      });
      setLastSaved(new Date());
    }

    setCurrentWeekStart(prev =>
      direction === 'prev' ? subWeeks(prev, 1) : addWeeks(prev, 1)
    );
  };

  const navigateToDate = (date: Date) => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);

    const latestEntries = entriesRef.current;
    const dirtyNow = !isReadOnly && (
      latestEntries.length !== currentTimesheet.entries.length ||
      JSON.stringify(latestEntries) !== JSON.stringify(currentTimesheet.entries)
    );
    if (dirtyNow && !isSaving && !isSubmitting) {
      const updated = { ...currentTimesheet, entries: latestEntries, status: 'draft' as const };
      flushSync(() => {
        updateTimesheet(updated);
      });
      setLastSaved(new Date());
    }

    const newWeekStart = startOfWeek(date, { weekStartsOn: weekStartDay });
    setCurrentWeekStart(newWeekStart);
  };

  // AI Feature Handlers
  const handleDismissWarning = useCallback((warningId: string) => {
    if (nonDismissableWarningIds.has(warningId)) {
      toast.warning('This alert cannot be dismissed because it is blocking submission per your organisation policy.');
      return;
    }
    setDismissedWarnings(prev => new Set([...prev, warningId]));
  }, [nonDismissableWarningIds]);

  const handleApplyAutoFill = (suggestions: any[]) => {
    const newEntries = suggestions.map((suggestion, index) => ({
      id: -Date.now() - index,
      projectId: suggestion.projectId,
      costCentreId: suggestion.costCentreId,
      description: isDaily ? '' : suggestion.description,
      dailyNotes: isDaily ? (suggestion.dailyNotes ? { ...suggestion.dailyNotes } : {}) : suggestion.dailyNotes,
      hours: suggestion.hours,
    }));
    setEntries(prevEntries => [...prevEntries, ...newEntries]);
  };

  const handleNaturalLanguageEntry = (entry: Partial<TimesheetEntry>) => {
    const newEntry: TimesheetEntry = {
      id: -Date.now(),
      projectId: entry.projectId || '',
      costCentreId: entry.costCentreId || '',
      description: entry.description || '',
      hours: entry.hours || {
        monday: 0,
        tuesday: 0,
        wednesday: 0,
        thursday: 0,
        friday: 0,
        saturday: 0,
        sunday: 0,
      },
    };
    setEntries(prevEntries => [...prevEntries, newEntry]);
  };

  // Scheduled booking handlers
  const handleDismissBooking = (index: number, reason: string, customReason?: string) => {
    setEntries(prevEntries => {
      const entry = prevEntries[index];
      const dismissedEntry = dismissBooking(entry, customReason || reason);
      const updated = [...prevEntries];
      updated[index] = dismissedEntry;
      return updated;
    });
  };

  const handleRestoreBooking = (index: number) => {
    setEntries(prevEntries => {
      const entry = prevEntries[index];
      const restoredEntry = restoreBooking(entry);
      const updated = [...prevEntries];
      updated[index] = restoredEntry;
      return updated;
    });
  };

  // Get rejection history for current timesheet
  const rejectionHistory = useMemo(() => {
    if (!currentTimesheet.id || currentTimesheet.id === 0) {
      return null;
    }
    return rejectionService.getTimesheetRejectionHistory(currentTimesheet.id);
  }, [currentTimesheet.id]);

  // Get the most recent pending rejection
  const pendingRejection = useMemo(() => {
    if (!rejectionHistory || rejectionHistory.rejections.length === 0) {
      return null;
    }
    return rejectionHistory.rejections.find(r => r.status === 'pending_correction') || null;
  }, [rejectionHistory]);

  // State for rejection details expansion
  const [showRejectionDetails, setShowRejectionDetails] = useState(true);

  // Derived read-only / status flags (needed early for isDirty and guards below)
  const isReadOnly = currentTimesheet.status !== 'draft' && currentTimesheet.status !== 'rejected' && currentTimesheet.status !== 'recalled';
  const isRejected = currentTimesheet.status === 'rejected' && pendingRejection;
  const isSubmitted = currentTimesheet.status === 'submitted';

  // Deadline warning state for the current week's timesheet
  const deadlineWarning = useMemo(() => {
    const deadlineSettings = organizationSettings?.deadline;
    if (!deadlineSettings) return null;

    // Only show warnings for draft timesheets
    if (currentTimesheet.status !== 'draft') return null;

    const now = new Date();
    const deadline = new Date(currentWeekStart);
    const baseDayOfWeek = deadline.getDay();
    const targetDayOfWeek = deadlineSettings.deadlineDay;
    const offset = (targetDayOfWeek - baseDayOfWeek + 7) % 7;
    deadline.setDate(deadline.getDate() + offset);
    const [hrs, mins] = (deadlineSettings.deadlineTime || '17:00').split(':').map(Number);
    deadline.setHours(hrs, mins, 0, 0);

    const diffMs = deadline.getTime() - now.getTime();
    const daysUntilDeadline = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const reminderDays = deadlineSettings.reminderDaysBefore ?? 2;

    if (daysUntilDeadline < 0) {
      const daysOverdue = Math.abs(daysUntilDeadline);
      return {
        type: 'overdue' as const,
        daysOverdue,
        deadline,
        message: `Your timesheet for the week of ${format(currentWeekStart, 'dd MMM yyyy')} is ${daysOverdue} day${daysOverdue > 1 ? 's' : ''} overdue.`,
      };
    }

    if (daysUntilDeadline <= reminderDays) {
      return {
        type: 'approaching' as const,
        daysUntilDeadline,
        deadline,
        message: daysUntilDeadline === 0
          ? `Your timesheet is due today by ${format(deadline, 'h:mm a')}.`
          : `Your timesheet is due in ${daysUntilDeadline} day${daysUntilDeadline > 1 ? 's' : ''} (${format(deadline, 'EEEE, dd MMM')} at ${format(deadline, 'h:mm a')}).`,
      };
    }

    return null;
  }, [organizationSettings?.deadline, currentTimesheet.status, currentWeekStart]);

  // Loading state to prevent double-click / multiple concurrent API requests
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Pre-stage rule confirmation dialog state
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [pendingConfirmTimesheetId, setPendingConfirmTimesheetId] = useState<number | null>(null);

  // ── Auto-save & dirty tracking ──────────────────────────────────────────
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tableScrollRef = useRef<HTMLDivElement>(null);

  // Always-current ref so event handlers (navigateWeek, saveTimesheet) never
  // read stale entries from a closed-over render snapshot.
  const entriesRef = useRef<TimesheetEntry[]>(entries);
  useEffect(() => {
    entriesRef.current = entries;
  }, [entries]);

  // isDirty: true when local entries differ from the last-persisted context copy
  const isDirty = useMemo(() => {
    if (isReadOnly) return false;
    if (entries.length !== currentTimesheet.entries.length) return true;
    return JSON.stringify(entries) !== JSON.stringify(currentTimesheet.entries);
  }, [entries, currentTimesheet.entries, isReadOnly]);

  // Browser tab / window close guard
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Handle resubmit
  const handleResubmit = async () => {
    if (isSaving || isSubmitting) return;

    if (entries.length === 0) {
      toast.error('Cannot submit empty timesheet');
      return;
    }

    if (hasBlockingPolicyViolation) {
      toast.error('Resubmission blocked by policy', {
        description: 'A policy violation is preventing resubmission. Please review AI Insights for details.',
        duration: 5000,
      });
      return;
    }

        if (organizationSettings?.requireDescription === 'always' && hasMissingDescriptions) {
          toast.error(
            isDaily
              ? 'Please add daily descriptions to all entries before submitting'
              : 'Please add descriptions to all entries'
          );
          return;
        }

    // Validate role requirement on resubmit
    if (organizationSettings?.showRole && organizationSettings?.requireRole) {
      const missingRoles = entries.filter(entry => !entry.roleId);
      if (missingRoles.length > 0) {
        toast.error('Cannot resubmit timesheet', {
          description: 'All entries must have a role assigned. Please add roles to all entries.',
          duration: 5000,
        });
        return;
      }
    }

    setIsSubmitting(true);
    try {
      // Update rejection status to resubmitted
      if (pendingRejection) {
        rejectionService.updateRejectionStatus(pendingRejection.id, 'resubmitted');
      }

      if (screenMode === 'live') {
        // LIVE MODE: Save entries first, then use the submit API endpoint
        // which triggers the workflow engine (same pattern as submitTimesheet)
        const timesheetId = currentTimesheet.id;

        // Save the updated entries to the server first
        // The PUT endpoint will also reset status from rejected → draft
        await timesheetApi.updateTimesheet(timesheetId, {
          ...currentTimesheet,
          entries,
        });

        // Now call the submit endpoint to trigger the workflow engine
        const submitResponse = await approvalsApi.submitTimesheet(timesheetId);

        if (submitResponse.message) {
          toast.success(submitResponse.message, {
            description: submitResponse.workflowInstanceId
              ? 'Workflow started successfully'
              : undefined,
            duration: 4000,
          });
        } else {
          toast.success('Timesheet resubmitted for approval');
        }

        // Fetch the updated timesheet from the server to get the latest state
        const updatedTimesheet = await timesheetApi.getTimesheetById(timesheetId);
        if (updatedTimesheet) {
          updateTimesheet(updatedTimesheet);
        }
      } else {
        // DEMO MODE: Update locally (existing behavior)
        const updated = {
          ...currentTimesheet,
          entries,
          status: 'submitted' as const,
          submittedAt: new Date()
        };
        updateTimesheet(updated);
        toast.success('Timesheet resubmitted for approval');
      }
    } catch (error) {
      console.error('Failed to resubmit timesheet:', error);
      toast.error('Failed to resubmit timesheet', {
        description: error instanceof Error ? error.message : 'An unexpected error occurred.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle recall timesheet
  const handleRecallTimesheet = async (timesheetId: number, reason: string): Promise<void> => {
    // Defensive check: only allow recall when awaiting approval (submitted)
    if (currentTimesheet.status?.toLowerCase() !== 'submitted') {
      toast.error('Cannot recall timesheet', {
        description: `Timesheet is currently ${currentTimesheet.status} and can only be recalled when submitted.`,
      });
      return;
    }
    await recallTimesheet(timesheetId, reason);
    // In demo mode, log a recalled audit entry before the status update
    if (screenMode !== 'live') {
      auditService.logAction(
        timesheetId,
        'recalled',
        currentEmployee.id,
        currentEmployee.name,
        {
          previousStatus: currentTimesheet.status,
          newStatus: 'draft',
          comment: reason,
          actorRole: 'employee',
        }
      );
    }
    // Refresh by updating the timesheet status locally
    const updated = {
      ...currentTimesheet,
      status: 'draft' as const,
    };
    updateTimesheet(updated);
  };
  
  // Helper function to format status for display
  const getFormattedStatus = () => {
    return currentTimesheet.status.charAt(0).toUpperCase() + currentTimesheet.status.slice(1);
  };

  // Auto-load / sync scheduled bookings when timesheet is opened.
  // For editable timesheets (draft/rejected/recalled): full 3-way sync so that
  //   booking updates and deletions are reflected in the timesheet lines.
  // For read-only timesheets (submitted/approved/locked): skip loading entirely
  //   so that no API calls are made and only persisted data is displayed.
  React.useEffect(() => {
    if (!hasLoadedScheduledBookings && entries.length >= 0) {
      // Auto-load / sync scheduled bookings when timesheet is opened.
      if (isReadOnly) {
        setHasLoadedScheduledBookings(true);
        return;
      }

      const weekEnding = addDays(currentWeekStart, 6);

      // Build employee role info for defaulting the Role field on booking-sourced entries
      const employeeRole: EmployeeRoleInfo = {
        roleId: currentEmployee?.roleId,
        roleName: currentEmployee?.roleId ? currentEmployee?.role : undefined,
      };

      // Helper: trigger cost centre lookups for any project codes that appear in
      // the reconciled entry set but were not previously fetched.
      const fetchNewProjectCostCentres = (reconciled: TimesheetEntry[]) => {
        const projectCodes = new Set<string>();
        reconciled.forEach(e => {
          if (e.projectCode) projectCodes.add(e.projectCode);
        });
        projectCodes.forEach(code => fetchCostCentresForProject(code));
      };

      if (screenMode === 'live') {
        syncScheduledBookingsAsync(targetEmployeeId, weekEnding, entries, employeeRole, currentCompany?.id)
          .then(reconciledEntries => {
            if (JSON.stringify(reconciledEntries) !== JSON.stringify(entries)) {
              setEntries(reconciledEntries);
              fetchNewProjectCostCentres(reconciledEntries);
            }
            setHasLoadedScheduledBookings(true);
          })
          .catch(error => {
            console.error('Failed to load scheduled bookings:', error);
            setHasLoadedScheduledBookings(true);
          });
      } else {
        const reconciledEntries = syncScheduledBookingsForWeek(targetEmployeeId, weekEnding, entries, employeeRole);

        if (JSON.stringify(reconciledEntries) !== JSON.stringify(entries)) {
          setEntries(reconciledEntries);
          fetchNewProjectCostCentres(reconciledEntries);
        }

        setHasLoadedScheduledBookings(true);
      }
    }
  }, [isReadOnly, hasLoadedScheduledBookings, entries.length, currentWeekStart, targetEmployeeId, screenMode, currentCompany?.id]);

  // Auto-load absences from the API when the timesheet week changes (live mode only).
  // Unlike scheduled bookings, absences are fetched for ALL timesheets (including
  // read-only/submitted/approved) because they are display-only supplementary data.
  // Uses employeeId + weekEnding (like scheduled bookings) instead of timesheetId
  // to avoid a timing issue where the timesheet ID is not yet available.
  React.useEffect(() => {
    if (hasLoadedAbsences) return;
    if (screenMode !== 'live') {
      setHasLoadedAbsences(true);
      return;
    }

    let cancelled = false;
    const weekEnding = addDays(currentWeekStart, 6);

    absencesApi.fetchAbsencesForWeek(targetEmployeeId, weekEnding, currentCompany?.id)
      .then(dtos => {
        if (cancelled) return;
        if (dtos.length === 0) {
          setHasLoadedAbsences(true);
          return;
        }

        const employeeRole: EmployeeRoleInfo = {
          roleId: currentEmployee?.roleId,
          roleName: currentEmployee?.roleId ? currentEmployee?.role : undefined,
        };
        const absenceEntries = dtos.map((dto, i) => mapAbsenceToTimesheetEntry(dto, i, employeeRole));

        setEntries(prev => {
          const existingAbsenceIds = new Set(
            prev.filter(e => e.absenceId).map(e => e.absenceId)
          );
          const newEntries = absenceEntries.filter(
            ae => !existingAbsenceIds.has(ae.absenceId)
          );
          // For entries that already exist, refresh hours from HR (source of truth)
          const updatedPrev = prev.map(e => {
            if (e.absenceId) {
              const freshAbsence = absenceEntries.find(ae => ae.absenceId === e.absenceId);
              if (freshAbsence) {
                return { ...e, hours: freshAbsence.hours, isReadOnly: true, sourceIntegration: freshAbsence.sourceIntegration };
              }
            }
            return e;
          });
          return newEntries.length > 0 ? [...updatedPrev, ...newEntries] : updatedPrev;
        });
        setHasLoadedAbsences(true);
      })
      .catch(() => {
        if (!cancelled) setHasLoadedAbsences(true);
      });

    return () => { cancelled = true; };
  }, [hasLoadedAbsences, currentWeekStart, targetEmployeeId, screenMode, currentCompany?.id]);

  return (
    <TooltipProvider>
      <div className="flex flex-col h-full overflow-hidden">
      {/* Sticky Top Section: Page Header + Week Toolbar */}
      <div className="flex-shrink-0 space-y-2 sm:space-y-3 px-0">

      {/* Page Header */}
      <div className="flex items-start gap-4 min-w-0">
          <div className="flex-1 min-w-0">
          <PageHeader
            title="Timesheets"

            size={36}
            showActions={true}
            showBreadcrumb={false}
            actions={
              currentTimesheet.id && currentTimesheet.id !== 0 ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAuditHistory(true)}
                  aria-label="View Timesheet History"
                  className="gap-1.5"
                  data-tour="timesheet-history"
                >
                  <History className="h-4 w-4" />
                  <span className="hidden sm:inline">History</span>
                </Button>
              ) : undefined
            }
          />
        </div>
        <div className="flex items-center gap-4 flex-shrink-0">
          <FeatureFeedbackCollector feature="Timesheets" />
        </div>
      </div>

      {/* Mode Indicator Banner */}
      {/* <ModeIndicatorBanner
        mode={mode}
        screenName="My Timesheet"
        onNavigateToSettings={() => {
          // Navigate to settings - you can implement this based on your routing
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      /> */}

      {/* Zone 1: Data Entry Toolbar - Redesigned Single Row Layout */}
      <div className="pb-2">
        <Card className="border-primary/20 bg-primary/5 shadow-md">
          <CardContent className="p-3 sm:p-4">
            {/* Desktop Layout (≥1024px) - Simplified Single Row */}
            <div className="hidden lg:flex items-center justify-between gap-3 w-full">
              {/* Week Selector */}
              <div className="flex items-center gap-2" data-tour="timesheet-week-nav">
                <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => navigateWeek('prev')}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="flex items-center gap-2 min-w-[280px]">
                  <h2 className="text-base font-semibold">
                    Week: {format(currentWeekStart, 'dd MMM')} - {format(addDays(currentWeekStart, 6), 'dd MMM yyyy')}
                  </h2>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Pick a date">
                        <Calendar className="h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={currentWeekStart}
                        onSelect={(date) => {
                          if (date) navigateToDate(date);
                        }}
                        disabled={(date) => {
                          if (date < minAllowedDate) return true;
                          if (maxAllowedDate && date > maxAllowedDate) return true;
                          return false;
                        }}
                        autoFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <Badge variant={
                    currentTimesheet.status === 'draft' ? 'secondary' :
                    currentTimesheet.status === 'submitted' ? 'default' :
                    currentTimesheet.status === 'approved' ? 'default' :
                    'secondary'
                  } className="text-xs w-[90px] justify-center" data-tour="timesheet-status">
                    {currentTimesheet.status.toUpperCase()}
                  </Badge>
                </div>
                <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => navigateWeek('next')}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>

              {/* Inline Alerts */}
              <div className="flex flex-col gap-1 flex-1 min-w-0">
                {isRejected && pendingRejection && (
                  <div className="flex items-start gap-2 border border-red-400 bg-red-50 dark:bg-red-950/20 rounded px-2.5 py-1.5">
                    <XCircle className="h-3 w-3 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                    <div className="flex flex-col min-w-0">
                      <span className="font-semibold text-red-900 dark:text-red-100 leading-tight" style={{ fontSize: '12px' }}>Timesheet Rejected – Action Required</span>
                      <span className="text-red-800 dark:text-red-200 leading-tight" style={{ fontSize: '12px' }}>{pendingRejection.rejectionReason}</span>
                    </div>
                  </div>
                )}
                {deadlineWarning && (
                  <div className={cn(
                    'flex items-start gap-2 border rounded px-2.5 py-1.5',
                    deadlineWarning.type === 'overdue'
                      ? 'border-red-400 bg-red-50 dark:bg-red-950/20'
                      : 'border-amber-400 bg-amber-50 dark:bg-amber-950/20'
                  )}>
                    {deadlineWarning.type === 'overdue'
                      ? <AlertCircle className="h-3 w-3 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                      : <Clock className="h-3 w-3 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    }
                    <div className="flex flex-col min-w-0">
                      <span className={cn('font-semibold leading-tight', deadlineWarning.type === 'overdue' ? 'text-red-900 dark:text-red-100' : 'text-amber-900 dark:text-amber-100')} style={{ fontSize: '12px' }}>
                        {deadlineWarning.type === 'overdue' ? 'Submission Overdue' : 'Submission Deadline Approaching'}
                      </span>
                      <span className={cn('leading-tight', deadlineWarning.type === 'overdue' ? 'text-red-800 dark:text-red-200' : 'text-amber-800 dark:text-amber-200')} style={{ fontSize: '12px' }}>
                        {deadlineWarning.message}
                      </span>
                    </div>
                  </div>
                )}
              </div>

            </div>

            {/* Tablet Layout (768px - 1023px) - Compact Single Row */}
            <div className="hidden md:flex lg:hidden items-center justify-between gap-2 w-full">
              {/* Week Selector */}
              <div className="flex items-center gap-2" data-tour="timesheet-week-nav">
                  <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => navigateWeek('prev')}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <div className="flex items-center gap-2 min-w-[240px]">
                    <h2 className="text-sm font-semibold">
                      Week: {format(currentWeekStart, 'dd MMM')} - {format(addDays(currentWeekStart, 6), 'dd MMM yyyy')}
                    </h2>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Pick a date">
                          <Calendar className="h-4 w-4" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={currentWeekStart}
                          onSelect={(date) => {
                            if (date) navigateToDate(date);
                          }}
                          disabled={(date) => {
                            if (date < minAllowedDate) return true;
                            if (maxAllowedDate && date > maxAllowedDate) return true;
                            return false;
                          }}
                          autoFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <Badge variant={
                      currentTimesheet.status === 'draft' ? 'secondary' :
                      currentTimesheet.status === 'submitted' ? 'default' :
                      currentTimesheet.status === 'approved' ? 'default' :
                      'secondary'
                    } className="text-xs w-[90px] justify-center" data-tour="timesheet-status">
                      {currentTimesheet.status.toUpperCase()}
                    </Badge>
                  </div>
                  <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => navigateWeek('next')}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>

              {/* Inline Alerts */}
              <div className="flex flex-col gap-1 flex-1 min-w-0">
                {isRejected && pendingRejection && (
                  <div className="flex items-center gap-1.5 border border-red-400 bg-red-50 dark:bg-red-950/20 rounded px-2 py-1 min-w-0">
                    <XCircle className="h-3 w-3 text-red-600 dark:text-red-400 flex-shrink-0" />
                    <span className="font-semibold text-red-900 dark:text-red-100 flex-shrink-0" style={{ fontSize: '12px' }}>Timesheet Rejected</span>
                    <span className="text-red-800 dark:text-red-200 truncate" style={{ fontSize: '12px' }}>· {pendingRejection.rejectionReason}</span>
                  </div>
                )}
                {deadlineWarning && (
                  <div className={cn(
                    'flex items-center gap-1.5 border rounded px-2 py-1 min-w-0',
                    deadlineWarning.type === 'overdue'
                      ? 'border-red-400 bg-red-50 dark:bg-red-950/20'
                      : 'border-amber-400 bg-amber-50 dark:bg-amber-950/20'
                  )}>
                    {deadlineWarning.type === 'overdue'
                      ? <AlertCircle className="h-3 w-3 text-red-600 dark:text-red-400 flex-shrink-0" />
                      : <Clock className="h-3 w-3 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                    }
                    <span className={cn('font-semibold flex-shrink-0', deadlineWarning.type === 'overdue' ? 'text-red-900 dark:text-red-100' : 'text-amber-900 dark:text-amber-100')} style={{ fontSize: '12px' }}>
                      {deadlineWarning.type === 'overdue' ? 'Overdue' : 'Deadline Approaching'}
                    </span>
                    <span className={cn('truncate', deadlineWarning.type === 'overdue' ? 'text-red-800 dark:text-red-200' : 'text-amber-800 dark:text-amber-200')} style={{ fontSize: '12px' }}>
                      · {deadlineWarning.message}
                    </span>
                  </div>
                )}
              </div>

            </div>

            {/* Mobile Layout (<768px) - Simplified */}
            <div className="flex md:hidden flex-col gap-3">
              {/* Week Selector */}
              <div className="flex items-center gap-2 justify-center" data-tour="timesheet-week-nav">
                  <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => navigateWeek('prev')}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <div className="flex items-center gap-2 min-w-[200px] justify-center">
                    <h2 className="text-sm font-semibold">
                      Week: {format(currentWeekStart, 'dd MMM')} - {format(addDays(currentWeekStart, 6), 'dd MMM yyyy')}
                    </h2>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Pick a date">
                          <Calendar className="h-4 w-4" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={currentWeekStart}
                          onSelect={(date) => {
                            if (date) navigateToDate(date);
                          }}
                          disabled={(date) => {
                            if (date < minAllowedDate) return true;
                            if (maxAllowedDate && date > maxAllowedDate) return true;
                            return false;
                          }}
                          autoFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <Badge variant={
                      currentTimesheet.status === 'draft' ? 'secondary' :
                      currentTimesheet.status === 'submitted' ? 'default' :
                      currentTimesheet.status === 'approved' ? 'default' :
                      'secondary'
                    } className="text-xs w-[90px] justify-center" data-tour="timesheet-status">
                      {currentTimesheet.status.toUpperCase()}
                    </Badge>
                  </div>
                  <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => navigateWeek('next')}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>

              {/* Inline Alerts */}
              {(isRejected && pendingRejection || deadlineWarning) && (
                <div className="flex flex-col gap-1">
                  {isRejected && pendingRejection && (
                    <div className="flex items-center gap-1.5 border border-red-400 bg-red-50 dark:bg-red-950/20 rounded px-2 py-1">
                      <XCircle className="h-3 w-3 text-red-600 dark:text-red-400 flex-shrink-0" />
                      <span className="font-semibold text-red-900 dark:text-red-100 flex-shrink-0" style={{ fontSize: '12px' }}>Timesheet Rejected</span>
                      <span className="text-red-800 dark:text-red-200 truncate" style={{ fontSize: '12px' }}>· {pendingRejection.rejectionReason}</span>
                    </div>
                  )}
                  {deadlineWarning && (
                    <div className={cn(
                      'flex items-center gap-1.5 border rounded px-2 py-1',
                      deadlineWarning.type === 'overdue'
                        ? 'border-red-400 bg-red-50 dark:bg-red-950/20'
                        : 'border-amber-400 bg-amber-50 dark:bg-amber-950/20'
                    )}>
                      {deadlineWarning.type === 'overdue'
                        ? <AlertCircle className="h-3 w-3 text-red-600 dark:text-red-400 flex-shrink-0" />
                        : <Clock className="h-3 w-3 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                      }
                      <span className={cn('font-semibold flex-shrink-0', deadlineWarning.type === 'overdue' ? 'text-red-900 dark:text-red-100' : 'text-amber-900 dark:text-amber-100')} style={{ fontSize: '12px' }}>
                        {deadlineWarning.type === 'overdue' ? 'Overdue' : 'Deadline Approaching'}
                      </span>
                      <span className={cn('truncate', deadlineWarning.type === 'overdue' ? 'text-red-800 dark:text-red-200' : 'text-amber-800 dark:text-amber-200')} style={{ fontSize: '12px' }}>
                        · {deadlineWarning.message}
                      </span>
                    </div>
                  )}
                </div>
              )}

            </div>
          </CardContent>
        </Card>
      </div>

      </div>{/* end flex-shrink-0 sticky top section */}

      {/* Smart Auto-Fill Dialog */}
      <SmartAutoFillDialog
        open={showAutoFillDialog}
        onOpenChange={setShowAutoFillDialog}
        suggestions={autoFillSuggestions}
        projects={projects}
        onApply={handleApplyAutoFill}
      />

      {/* Scrollable content area */}
      <div className="flex-1 min-h-0 flex flex-col gap-2 sm:gap-3 pb-1">

      {/* Grid */}
      <Card data-tour="timesheet-grid" className="max-h-full flex flex-col">
        <CardContent className="p-0 flex-1 flex flex-col min-h-0 overflow-hidden">
          <div ref={tableScrollRef} className="flex-1 overflow-x-auto overflow-y-auto min-h-0">
            <table className="w-full border-collapse text-sm">
              <thead className="sticky top-0 z-10 bg-background">
                <tr className="border-b bg-muted">
                  <th className="p-1.5 sm:p-2 text-left font-medium text-xs sm:text-sm min-w-[140px]">
                    <div className="flex items-center flex-wrap gap-x-3 gap-y-1">
                      <span>Project{!isReadOnly && <span className="text-destructive ml-0.5">*</span>}</span>
                      {/* Status Legend — inline to the right of Project title */}
                      <div className="flex items-center gap-2 font-normal text-[11px] text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <div className="w-1 h-3.5 rounded-full" style={{ backgroundColor: 'var(--success-400)' }} aria-hidden="true" />
                          <span>Billable</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-1 h-3.5 border-l-4 border-double" style={{ borderLeftColor: 'var(--info-400)' }} aria-hidden="true" />
                          <span>Scheduled</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-1 h-3.5 border-l-4 border-dotted" style={{ borderLeftColor: 'var(--electricviolet-400)' }} aria-hidden="true" />
                          <span>Absence</span>
                        </div>
                        {nonWorkingDays.size > 0 && (
                          <div className="flex items-center gap-1">
                            <div className="w-3.5 h-3.5 rounded-sm bg-amber-50 border border-amber-200 dark:bg-amber-900/30 dark:border-amber-700" aria-hidden="true" />
                            <span>Non-working</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </th>
                  {/* Cost Centre column */}
                  {organizationSettings?.showCostCentre !== false && (
                    <th className="p-1.5 sm:p-2 text-left font-medium text-xs sm:text-sm min-w-[120px]">
                      Cost Centre{organizationSettings?.requireCostCentre && !isReadOnly && <span className="text-destructive ml-0.5">*</span>}
                    </th>
                  )}
                  {/* Role column */}
                  {organizationSettings?.showRole && (
                    <th className="p-1.5 sm:p-2 text-left font-medium text-xs sm:text-sm min-w-[100px]">
                      Role{organizationSettings?.requireRole && !isReadOnly && <span className="text-destructive ml-0.5">*</span>}
                    </th>
                  )}
                  {/* Work Type column */}
                  {overtimeEnabled && (
                    <th className="p-1.5 sm:p-2 text-left font-medium text-xs sm:text-sm min-w-[110px]">
                      Work Type
                    </th>
                  )}
                  {!isDaily && (
                    <th className="p-1.5 sm:p-2 text-left font-medium text-xs sm:text-sm min-w-[100px]">
                      Description{organizationSettings?.requireDescription === 'always' && !isReadOnly && <span className="text-destructive ml-0.5">*</span>}
                    </th>
                  )}
                  {VISIBLE_DAYS.map((day, i) => {
                    // Calculate the actual date for this day based on its position in the week
                    const dayIndex = ALL_DAYS.indexOf(day);
                    const weekStartDayIndex = weekStartDay;
                    const daysFromStart = dayIndex >= weekStartDayIndex 
                      ? dayIndex - weekStartDayIndex 
                      : 7 - weekStartDayIndex + dayIndex;
                    const cellDate = addDays(currentWeekStart, daysFromStart);
                    const isNonWorking = nonWorkingDays.has(day);
                    
                    return (
                      <th key={day} className={cn(
                        'p-1.5 sm:p-2 text-center font-medium text-xs sm:text-sm',
                        isDaily ? 'w-[80px] sm:w-[100px]' : 'w-[54px] sm:w-[60px]',
                        isWeekendDay(day) && 'bg-muted',
                        isNonWorking && 'bg-amber-50/60 dark:bg-amber-900/20'
                      )}>
                        {format(cellDate, 'EEE')}
                        <div className="text-[10px] sm:text-xs font-normal text-muted-foreground">
                          {format(cellDate, 'dd/MM')}
                        </div>
                        {isNonWorking && (
                          <div className="text-[9px] font-normal text-amber-600 dark:text-amber-400 leading-tight">
                            Non-working
                          </div>
                        )}
                      </th>
                    );
                  })}
                  <th className={`p-1.5 sm:p-2 text-center font-medium text-xs sm:text-sm ${isDaily ? 'w-[64px] sm:w-[72px]' : 'w-[52px] sm:w-[56px]'}`}>Total</th>
                  <th className="p-1.5 sm:p-2 text-center font-medium text-xs sm:text-sm w-[40px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 && (
                  <tr>
                    <td colSpan={20} className="text-center align-middle py-12">
                      <div className="flex flex-col items-center gap-4">
                        <div className="rounded-full bg-muted p-4">
                          <Calendar className="h-8 w-8 text-muted-foreground" />
                        </div>
                        <div className="space-y-1">
                          <p className="text-sm font-medium text-foreground">No timesheet entries yet</p>
                          <p className="text-xs text-muted-foreground">Add your first row to start logging your time for this week</p>
                        </div>

                      </div>
                    </td>
                  </tr>
                )}
                {entries.map((entry, index) => {
                  const project = allAvailableProjects.find(p => p.id === entry.projectId);
                  // Get cost centres filtered by project code (if available)
                  // In live mode, these are fetched from the API filtered by project code
                  // In demo mode, all cost centres are used (no filtering)
                  const projectCode = project?.code || entry.projectCode;
                  const projectCostCentres = projectCode
                    ? (costCentresByProjectCode[projectCode] || [])
                    : [];
                  // Merge API cost centres (which include description, status, userSubtotal)
                  // with local enrichments (isFavourite, lastUsed) from global context
                  const enrichedCostCentres = projectCostCentres.map(cc => {
                    const enriched = costCentres.find(ecc => ecc.id === cc.id);
                    if (!enriched) return cc;
                    return { ...cc, isFavourite: enriched.isFavourite, lastUsed: enriched.lastUsed };
                  });
                  // Check if cost centres are still loading for this project
                  const isLoadingCostCentres = projectCode
                    ? loadingCostCentresForProject[projectCode] || false
                    : false;
                  
                  // Check if this is a scheduled entry
                  const isScheduled = entry.isFromSchedule || false;
                  const isDismissed = entry.isDismissed || false;
                  const isEdited = isScheduled && !isDismissed && hasEditedHours(entry);
                  
                  // Check if this is an absence entry from HR system
                  const isAbsence = entry.sourceIntegration === 'people-hr';
                  
                  // Debug logging - log ALL entries to see what we have
                  const isAbsenceReadOnly = isAbsence && entry.isReadOnly;
                  
                  const absenceType: AbsenceType | null = isAbsence
                    ? (entry.absenceType as AbsenceType) ?? null
                    : null;
                  
                  // Determine status dot type
                  const statusType: StatusDotType = isDismissed 
                    ? 'dismissed' 
                    : isEdited 
                    ? 'edited' 
                    : 'scheduled';
                  
                  return (
                    <tr 
                      key={entry.id}
                      className={cn(
                        "border-b hover:bg-muted/50",
                        // Absence entry styling (takes priority) - purple dotted left border
                        isAbsence && "bg-purple-50/30 hover:bg-purple-50/50 border-l-4 border-dotted",
                        // Scheduled entry styling (pale blue shade) - blue double left border
                        !isAbsence && isScheduled && "bg-blue-50/40 hover:bg-blue-50/60 border-l-4 border-double",
                        // Billable entry styling (green solid left accent) - only for non-scheduled, non-absence entries
                        !isAbsence && !isScheduled && project?.billable && "border-l-4"
                      )}
                      style={{
                        borderLeftColor: isAbsence 
                          ? 'var(--electricviolet-400)' 
                          : isScheduled 
                          ? 'var(--info-400)' 
                          : project?.billable 
                          ? 'var(--success-400)' 
                          : undefined
                      }}
                      aria-label={
                        isAbsence 
                          ? `Absence entry: ${entry.description}, read-only`
                          : project 
                          ? `${project.name}, ${project.billable ? 'billable' : 'non-billable'} project${isScheduled ? `, scheduled booking (${statusType})` : ', manual entry'}` 
                          : 'No project selected'
                      }
                    >
                      <td className="p-1 sm:p-1.5">
                        {/* Show project selector for absence entries */}
                        {isAbsence ? (
                          <div className="flex items-center gap-2">
                            <div className="flex-1">
                              <ProjectSelector
                                projects={allAvailableProjects}
                                value={entry.projectId}
                                onChange={() => {}} // No-op for absence entries
                                disabled={true}
                                // Server-side search props
                                onServerSearch={handleProjectSearch}
                                isSearching={isSearchingProjects}
                                companyId={currentCompany?.id}
                                fallbackName={entry.projectName}
                                fallbackCode={entry.projectCode}
                              />
                            </div>
                            {allAvailableProjects.find(p => p.id === entry.projectId)?.isFavourite && (
                              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400 flex-shrink-0" />
                            )}
                          </div>
                        ) : isScheduled ? (
                          <div className="flex items-center gap-2">
                            <div className="flex-1">
                              <BookingHoverCard
                                booking={entry}
                                statusType={statusType}
                              >
                                <ProjectSelector
                                      projects={allAvailableProjects}
                                      value={entry.projectId}
                                      onChange={(projectId) => {
                                        updateEntry(index, 'projectId', projectId);
                                        // Clear cost centre when project changes - user must select a new one
                                        updateEntry(index, 'costCentreId', '');
                                        // Fetch filtered cost centres for the new project
                                        const proj = allAvailableProjects.find(p => p.id === projectId);
                                        if (proj?.code) {
                                          fetchCostCentresForProject(proj.code);
                                        }
                                      }}
                                      disabled={isReadOnly}
                                      required={true}
                                      isSubmitted={isReadOnly}
                                      // Server-side search props
                                      onServerSearch={handleProjectSearch}
                                      isSearching={isSearchingProjects}
                                      companyId={currentCompany?.id}
                                      fallbackName={entry.projectName}
                                      fallbackCode={entry.projectCode}
                                    />
                              </BookingHoverCard>
                            </div>
                            {allAvailableProjects.find(p => p.id === entry.projectId)?.isFavourite && (
                              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400 flex-shrink-0" />
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <div className="flex-1">
                              <ProjectSelector
                                    projects={allAvailableProjects}
                                    value={entry.projectId}
                                    onChange={(projectId) => {
                                      updateEntry(index, 'projectId', projectId);
                                      // Clear cost centre when project changes - user must select a new one
                                      updateEntry(index, 'costCentreId', '');
                                      // Fetch filtered cost centres for the new project
                                      const proj = allAvailableProjects.find(p => p.id === projectId);
                                      if (proj?.code) {
                                        fetchCostCentresForProject(proj.code);
                                      }
                                    }}
                                    disabled={isReadOnly}
                                    required={true}
                                    isSubmitted={isReadOnly}
                                    // Server-side search props
                                    onServerSearch={handleProjectSearch}
                                    isSearching={isSearchingProjects}
                                    companyId={currentCompany?.id}
                                    fallbackName={entry.projectName}
                                    fallbackCode={entry.projectCode}
                                  />
                            </div>
                            {allAvailableProjects.find(p => p.id === entry.projectId)?.isFavourite && (
                              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400 flex-shrink-0" />
                            )}
                          </div>
                        )}
                      </td>
                      {organizationSettings?.showCostCentre !== false && (
                        <td className="p-1 sm:p-1.5">
                          <div className="flex items-center gap-2">
                            <div className="flex-1">
                              <CostCentreSelector
                                costCentres={enrichedCostCentres}
                                value={entry.costCentreId}
                                onChange={(value) => {
                                  if (!isAbsence) {
                                    updateEntry(index, 'costCentreId', value);
                                    updateCostCentreLastUsed(value);
                                  }
                                }}
                                disabled={isAbsence || isReadOnly || (!project && !entry.projectId)}
                                fallbackName={entry.costCentreName}
                                fallbackCode={entry.costCentreCode}
                                required={organizationSettings?.requireCostCentre}
                                isSubmitted={isReadOnly}
                              />
                            </div>
                            {enrichedCostCentres.find(c => c.id === entry.costCentreId)?.isFavourite && (
                              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400 flex-shrink-0" />
                            )}
                          </div>
                        </td>
                      )}
                      {organizationSettings?.showRole && (
                        <td className="p-1 sm:p-1.5">
                          <RoleSelector
                            value={entry.roleId}
                            onChange={(roleId, roleName) => {
                              if (!isAbsence) {
                                updateEntry(index, 'roleId', roleId);
                                updateEntry(index, 'roleName', roleName);
                              }
                            }}
                            disabled={isAbsence || isReadOnly}
                            required={organizationSettings?.requireRole}
                            screenMode={screenMode}
                            displayName={entry.roleName}
                            isSubmitted={isReadOnly}
                          />
                        </td>
                      )}
                      {/* Work Type cell */}
                      {overtimeEnabled && (
                        <td className="p-1 sm:p-1.5">
                          <OvertimeTypeCell
                            entry={entry}
                            overtimeTypes={activeOvertimeTypes}
                            isReadOnly={isReadOnly}
                            isAbsence={isAbsence}
                            onChangeOvertimeType={(typeId) => {
                              const ot = activeOvertimeTypes.find(t => t.id === typeId);
                              updateEntry(index, 'overtimeTypeId', typeId);
                              updateEntry(index, 'overtimeTypeName', ot?.name);
                              updateEntry(index, 'overtimeCostMultiplier', ot?.costMultiplier);
                              updateEntry(index, 'overtimeBillMultiplier', ot?.billMultiplier);
                            }}
                          />
                        </td>
                      )}
                      {!isDaily && (
                        <td className="p-1 sm:p-1.5">
                          {isAbsence ? (
                            <Textarea
                              value={entry.description}
                              onChange={() => {}} // No-op for absence entries
                              placeholder="Activity description..."
                              className="h-9 min-h-9 resize-none text-xs sm:text-sm bg-muted/50 cursor-not-allowed"
                              disabled={true}
                            />
                          ) : (
                            <Textarea
                              value={entry.description}
                              onChange={(e) => updateEntry(index, 'description', e.target.value)}
                              placeholder="Activity description..."
                              className={cn(
                                'h-9 min-h-9 resize-none text-xs sm:text-sm',
                                organizationSettings?.requireDescription === 'always' && !entry.description.trim() && !isReadOnly && 'border-destructive/50 focus:border-destructive focus:ring-destructive/30'
                              )}
                              disabled={isReadOnly}
                            />
                          )}
                        </td>
                      )}
                      {VISIBLE_DAYS.map((day, i) => {
                        const cellKey = getHoursCellKey(entry.id, day);
                        const isEditing = editingHours[cellKey] !== undefined;
                        const displayValue = isEditing
                          ? editingHours[cellKey]!
                          : entry.hours[day] === 0
                            ? ''
                            : entry.hours[day].toFixed(2);
                        


                        return (
                          <td key={day} className={cn(
                            'p-1',
                            isWeekendDay(day) && 'bg-muted/30',
                            nonWorkingDays.has(day) && 'bg-amber-50/40 dark:bg-amber-900/10',
                          )}>
                            <div className={cn("flex flex-row items-center gap-0.5", isDaily ? "w-full" : "")}>
                            <Input
                              data-tour="timesheet-hours-cell"
                              type="text"
                              inputMode="decimal"
                              value={displayValue}
                              onChange={(e) => {
                                if (!isAbsence) {
                                  setEditingHours(prev => ({ ...prev, [cellKey]: e.target.value }));
                                }
                              }}
                              onFocus={(e) => {
                                // Select all text on focus for easy overwriting (Excel-like behavior)
                                e.target.select();
                              }}
                              onBlur={(e) => {
                                if (!isAbsence) {
                                  const raw = e.target.value;
                                  let hours = raw === '' ? 0 : parseFloat(raw);
                                  if (isNaN(hours)) hours = 0;
                                  // Apply rounding based on settings
                                  const timeRounding = organizationSettings?.timeRounding ?? 'none';
                                  let step = 0;
                                  if (timeRounding === '15min') step = 0.25;
                                  else if (timeRounding === '30min') step = 0.5;
                                  else if (timeRounding === '1hour') step = 1;
                                  if (step > 0) {
                                    hours = Math.round(hours / step) * step;
                                  }
                                  // Enforce minimum entry duration (if non-zero)
                                  const minSetting = organizationSettings?.minimumEntryDuration ?? '6min';
                                  let min = 0.1; // 6min
                                  if (minSetting === '15min') min = 0.25;
                                  else if (minSetting === '30min') min = 0.5;
                                  else if (minSetting === '1hour') min = 1;
                                  if (hours !== 0 && Math.abs(hours) < min) {
                                    hours = hours < 0 ? -min : min;
                                  }
                                  // Clamp based on allowNegativeHours
                                  const allowNeg = organizationSettings?.allowNegativeHours ?? false;
                                  const clamped = allowNeg ? Math.max(-24, Math.min(24, hours)) : Math.max(0, Math.min(24, hours));
                                  updateHours(index, day, String(clamped));
                                  setEditingHours(prev => {
                                    const next = { ...prev };
                                    delete next[cellKey];
                                    return next;
                                  });
                                  // Auto-open daily note popover when:
                                  // - daily description mode is on
                                  // - description is required (always)
                                  // - hours were just entered (> 0)
                                  // - there is no existing note for this day
                                  // - the previous value was 0 (new entry, not editing existing)
                                  const prevHours = entry.hours[day] ?? 0;
                                  const hasExistingNote = (entry.dailyNotes?.[day] ?? '').trim().length > 0;
                                  if (
                                    isDaily &&
                                    organizationSettings?.requireDescription === 'always' &&
                                    clamped > 0 &&
                                    prevHours === 0 &&
                                    !hasExistingNote
                                  ) {
                                    const noteKey = `${entry.id}-${day}`;
                                    // Reset draft so the new popover doesn't carry over a previous note
                                    setDraftNote('');
                                    // Small timeout so the input blur completes before the popover opens
                                    setTimeout(() => setOpenNoteCell(noteKey), 50);
                                  }
                                }
                              }}
                              onKeyDown={(e) => {
                                // Allow keyboard navigation like Excel
                                // Enter: commit value and blur (no native focus movement)
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  e.currentTarget.blur();
                                }
                                // Tab: let the browser handle natively — onBlur fires
                                // automatically when focus moves to the next field.
                                // Calling blur() here breaks Firefox's sequential
                                // focus navigation.
                                // Allow arrow keys for increment/decrement
                                if (e.key === 'ArrowUp') {
                                  e.preventDefault();
                                  const current = parseFloat(displayValue) || 0;
                                  const newValue = Math.min(24, current + 0.5);
                                  setEditingHours(prev => ({ ...prev, [cellKey]: newValue.toFixed(1) }));
                                }
                                if (e.key === 'ArrowDown') {
                                  e.preventDefault();
                                  const current = parseFloat(displayValue) || 0;
                                  const newValue = Math.max(0, current - 0.5);
                                  setEditingHours(prev => ({ ...prev, [cellKey]: newValue.toFixed(1) }));
                                }
                              }}
                              placeholder={entry.hours[day] === 0 ? '0' : ''}
                              className={cn(
                                "text-center text-sm h-9 px-1",
                                isDaily ? "flex-1 min-w-0" : "w-full",
                                "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                entry.hours[day] > 0 ? "text-foreground" : "text-muted-foreground/40",
                                (isReadOnly || isAbsence) && "bg-muted/50 cursor-not-allowed",
                                !isReadOnly && !isAbsence && "hover:border-primary/50 hover:bg-accent/30 transition-colors cursor-text",
                                isAbsence && entry.hours[day] > 0 && "bg-purple-50/40 border-purple-200"
                              )}
                              disabled={isReadOnly || isAbsence}
                            />
                            {isDaily && isAbsence && <div className="w-5 h-5 shrink-0" />}
                            {isDaily && !isAbsence && (entry.hours[day] > 0 ? (
                              (() => {
                                const noteKey = `${entry.id}-${day}`;
                                const existingNote = entry.dailyNotes?.[day] ?? '';
                                const hasNote = existingNote.trim().length > 0;
                                const isRequired = organizationSettings?.requireDescription === 'always';
                                const isOpen = openNoteCell === noteKey;
                                // Derive display date for header
                                const dayIndex = ALL_DAYS.indexOf(day);
                                const weekStartDayIndex = weekStartDay;
                                const daysFromStart = dayIndex >= weekStartDayIndex
                                  ? dayIndex - weekStartDayIndex
                                  : 7 - weekStartDayIndex + dayIndex;
                                const cellDate = addDays(currentWeekStart, daysFromStart);
                                return (
                                  <Popover
                                    open={isOpen}
                                    onOpenChange={(open) => {
                                      if (!open && isOpen) {
                                        // Save draft on close
                                        updateEntry(index, 'dailyNotes', { ...entry.dailyNotes, [day]: draftNote });
                                      }
                                      setOpenNoteCell(open ? noteKey : null);
                                      if (open) setDraftNote(existingNote);
                                    }}
                                  >
                                    <PopoverTrigger asChild>
                                      <button
                                        className={cn(
                                          'flex items-center justify-center rounded p-0.5 transition-colors shrink-0',
                                          hasNote
                                            ? 'text-primary hover:text-primary/80'
                                            : isRequired
                                              ? 'text-destructive ring-1 ring-destructive/40 rounded hover:text-destructive/80'
                                              : 'text-muted-foreground/30 hover:text-muted-foreground/70'
                                        )}
                                        onClick={() => {
                                          setOpenNoteCell(noteKey);
                                          setDraftNote(existingNote);
                                        }}
                                        type="button"
                                      >
                                        <MessageSquare className={cn('h-3 w-3', hasNote && 'fill-current')} />
                                      </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-72 p-3" align="center" side="bottom">
                                      <div className="space-y-2">
                                        <p className="text-xs font-medium text-foreground">
                                          {format(cellDate, 'EEEE d MMM')} · {allAvailableProjects.find(p => p.id === entry.projectId)?.name || 'No project selected'}
                                        </p>
                                        <Textarea
                                          autoFocus
                                          value={draftNote}
                                          onChange={(e) => setDraftNote(e.target.value)}
                                          placeholder="Add a note for this day..."
                                          className="h-20 min-h-[80px] resize-none text-xs"
                                        />
                                        <div className="flex justify-end">
                                          <Button
                                            size="sm"
                                            className="h-7 text-xs"
                                            onClick={() => {
                                              updateEntry(index, 'dailyNotes', { ...entry.dailyNotes, [day]: draftNote });
                                              setOpenNoteCell(null);
                                            }}
                                          >
                                            Done
                                          </Button>
                                        </div>
                                      </div>
                                    </PopoverContent>
                                  </Popover>
                                );
                              })()
                            ) : (
                              <div className="w-5 h-5 shrink-0" />
                            ))}
                            </div>
                          </td>
                        );
                      })}
                      <td className={cn(
                        "p-1 sm:p-1.5 text-center font-bold text-sm",
                        rowTotals[index] > 0 ? "text-foreground bg-muted/30" : "text-muted-foreground/50"
                      )}>
                        {rowTotals[index].toFixed(2)}
                      </td>
                      <td className="p-1 sm:p-1.5">
                        <div className="flex items-center justify-center gap-0.5 sm:gap-1">
                          {/* No actions for absence entries */}
                          {isAbsence ? (
                            <AbsenceInlineActions
                              onViewDetails={() => setSelectedAbsenceForDetails(entry)}
                            />
                          ) : (
                            <>
                              {/* Scheduled Booking Inline Actions */}
                              {isScheduled && (
                                <BookingInlineActions
                                  booking={entry}
                                  onDuplicate={canDuplicateTimesheet() ? () => duplicateRow(index) : undefined}
                                  onDelete={canDeleteTimesheet() ? () => deleteRow(index) : undefined}
                                  onViewDetails={() => setSelectedBookingForDetails(entry)}
                                  disabled={isReadOnly}
                                  readOnlyMode={isReadOnly}
                                />
                              )}
                              
                              {/* Manual Entry Inline Actions — always render for manual entries so the menu icon is consistent */}
                              {!isScheduled && (
                                <ManualEntryInlineActions
                                  onDuplicate={!isReadOnly && canDuplicateTimesheet() ? () => duplicateRow(index) : undefined}
                                  onDelete={!isReadOnly && canDeleteTimesheet() ? () => deleteRow(index) : undefined}
                                  disabled={isReadOnly}
                                  onClassifyOvertime={
                                    !isReadOnly && overtimeEnabled && activeOvertimeTypes.length > 0
                                      ? () => setClassifyOvertimeEntry(entry)
                                      : undefined
                                  }
                                />
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="sticky bottom-0 z-10 bg-background">
                <tr className="border-t-2 bg-muted font-bold">
                  <td className="p-2 sm:p-2.5">
                    {!isReadOnly && (
                      <div className="flex items-center gap-1.5">
                        <Button
                          onClick={addRow}
                          variant="outline"
                          size="default"
                          className="gap-1.5"
                          title="Add a new timesheet row (Alt+A)"
                          data-tour="timesheet-add-row"
                        >
                          <Plus className="h-4 w-4" />
                          Add Row
                        </Button>
                        {enableCopyPreviousWeek && (
                          <Button
                            onClick={copyFromLastWeek}
                            variant="outline"
                            size="default"
                            className="gap-1.5"
                          >
                            <Copy className="h-4 w-4" />
                            Copy Last Week
                          </Button>
                        )}
                        {enableAutoSmartFill && (
                          <Button
                            onClick={() => setShowAutoFillDialog(true)}
                            variant="outline"
                            size="default"
                            className="gap-1.5"
                          >
                            <Wand2 className="h-4 w-4" />
                            Smart Auto-Fill
                          </Button>
                        )}
                      </div>
                    )}
                  </td>
                  <td 
                    colSpan={
                      (organizationSettings?.showRole ? 1 : 0) + 
                      (organizationSettings?.showCostCentre !== false ? 1 : 0) + 
                      (overtimeEnabled ? 1 : 0) +
                      1
                    } 
                    className="p-2 sm:p-2.5 text-right text-xs sm:text-sm"
                  >
                    Daily Totals:
                  </td>
                  {columnTotals.map((total, i) => {
                    const day = VISIBLE_DAYS[i];
                    const dayBreaches = daysExceedingThreshold.has(day);
                    const isNonWorking = nonWorkingDays.has(day);
                    return (
                      <td key={i} className={cn(
                        "p-2 sm:p-2.5 text-center text-xs sm:text-sm font-medium",
                        isWeekendDay(day) && 'bg-muted',
                        isNonWorking && !dayBreaches && 'bg-amber-50/60 dark:bg-amber-900/20',
                        dayBreaches
                          ? 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300'
                          : total > 0 ? 'text-foreground' : ''
                      )}>
                        {total.toFixed(2)}
                        
                      </td>
                    );
                  })}
                  <td className={weekExceedsThreshold
                    ? "p-2 sm:p-2.5 text-center text-sm font-bold bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300"
                    : "p-2 sm:p-2.5 text-center text-sm font-bold bg-primary/10 text-primary"
                  }>
                    {weekTotal.toFixed(2)}
                    {weekExceedsThreshold ? <div className="text-[9px] font-normal leading-none mt-0.5 opacity-75">OT threshold</div> : null}
                  </td>
                  <td className="p-2 sm:p-2.5"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* AI Anomaly Warnings - Positioned near submit for final review */}
      {/* COMMENTED OUT: Replaced with AIInsightsSheet in sticky footer
      {aiWarnings.length > 0 && (
        <AnomalyAlertsPanelEnhanced
          warnings={aiWarnings}
          onDismiss={handleDismissWarning}
        />
      )}
      */}

      {/* Zone 3: Review & Submission Footer (Sticky) */}
      </div>{/* end scrollable content area */}

      {/* Submit footer - always visible at bottom */}
      {(!isReadOnly || isSubmitted || aiWarnings.length > 0) && (
        <div className="flex-shrink-0 bg-background border-t border-border">
          <div className="px-3 pt-2 pb-3 sm:px-4 sm:pt-2 sm:pb-4">
              <div className="flex items-center gap-3 justify-between">
                {/* Left: Validation messages */}
                <div className="flex-1 min-w-0">
                  {!isReadOnly && (
                    <>
                      {entries.length === 0 && (
                        <p className="text-xs text-muted-foreground truncate">Add at least one timesheet entry to submit</p>
                      )}
                      {entries.length > 0 && hasMissingProjects && (
                        <p className="text-xs text-destructive truncate">Please select a project for all entries before submitting</p>
                      )}
                      {entries.length > 0 && !hasMissingProjects && hasMissingDescriptions && !isDaily && (
                        <p className="text-xs text-destructive truncate">Please add descriptions to all entries before submitting</p>
                      )}
                      {entries.length > 0 && !hasMissingProjects && hasMissingDescriptions && isDaily && (
                        <p className="text-xs text-destructive truncate">Please add daily descriptions to all entries before submitting</p>
                      )}
                      {entries.length > 0 && !hasMissingProjects && hasMissingRoles && (
                        <p className="text-xs text-destructive truncate">Please add roles to all entries before submitting</p>
                      )}
                      {hasBlockingPolicyViolation && (
                        <p className="text-xs text-destructive flex items-center gap-1 truncate">
                          <Lock className="h-3 w-3 flex-shrink-0" />
                          Submission blocked by policy — review AI Insights for details
                        </p>
                      )}
                    </>
                  )}
                </div>
                {/* Right: Action Buttons */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* AI Insights Button */}
                  {enableAiInsights && aiWarnings.length > 0 && (
                    <Button 
                      variant="outline" 
                      size="default"
                      onClick={() => setShowAIInsightsSheet(true)}
                      className="flex-1 sm:flex-none gap-2"
                    >
                      <Sparkles className="h-4 w-4" />
                      <span>Insights</span>
                    </Button>
                  )}
                  
                  {/* Recall Button - Only show when timesheet is awaiting approval (submitted) */}
                  {currentTimesheet.status?.toLowerCase() === 'submitted' && (
                    <RecallTimesheetDialog
                      timesheet={currentTimesheet}
                      onRecall={handleRecallTimesheet}
                      disabled={!canRecallTimesheet()}
                    />
                  )}

                  {/* Save/Submit Buttons - Only show when NOT readonly */}
                  {!isReadOnly && (
                    <>
                      {/* Saved / unsaved indicator */}
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground flex-shrink-0">
                        {isSaving ? (
                          <span className="flex items-center gap-1.5 text-muted-foreground">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Saving…
                          </span>
                        ) : isDirty ? (
                          <span className="flex items-center gap-1.5 text-amber-600">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            Unsaved changes
                          </span>
                        ) : lastSaved ? (
                          <span className="flex items-center gap-1.5 text-green-600">
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            Saved {formatDistanceToNow(lastSaved, { addSuffix: true })}
                          </span>
                        ) : null}
                      </div>
                      <Button 
                        variant="outline" 
                        size="default"
                        onClick={saveTimesheet}
                        disabled={isSaving || isSubmitting || !isDirty}
                        className="flex-1 sm:flex-none gap-2"
                      >
                        {isSaving ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4" />
                        )}
                        {isSaving ? 'Saving...' : 'Save Draft'}
                      </Button>
                      {isRejected ? (
                        hasBlockingPolicyViolation ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span tabIndex={0} className="inline-flex cursor-not-allowed flex-1 sm:flex-none">
                                <Button 
                                  size="default"
                                  disabled
                                  className="w-full gap-2 font-semibold bg-orange-600/50 pointer-events-none"
                                >
                                  <Lock className="h-4 w-4" />
                                  Resubmit for Approval
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs text-center">
                              <p className="font-semibold">Submission Blocked</p>
                              <p className="text-xs mt-1">A policy violation is preventing submission. Open AI Insights for details.</p>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <Button 
                            size="default"
                            onClick={handleResubmit}
                            disabled={entries.length === 0 || hasMissingProjects || hasMissingDescriptions || hasMissingRoles || isSaving || isSubmitting}
                            className="flex-1 sm:flex-none gap-2 font-semibold bg-orange-600 hover:bg-orange-700"
                          >
                            {isSubmitting ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <RefreshCw className="h-4 w-4" />
                            )}
                            {isSubmitting ? 'Submitting...' : 'Resubmit for Approval'}
                          </Button>
                        )
                      ) : hasBlockingPolicyViolation ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span tabIndex={0} className="inline-flex cursor-not-allowed flex-1 sm:flex-none">
                              <Button 
                                size="default"
                                disabled
                                className="w-full gap-2 font-semibold pointer-events-none"
                                data-tour="timesheet-submit"
                              >
                                <Lock className="h-4 w-4" />
                                Submit for Approval
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-xs text-center">
                            <p className="font-semibold">Submission Blocked</p>
                            <p className="text-xs mt-1">A policy violation is preventing submission. Open AI Insights for details.</p>
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <Button 
                          size="default"
                          onClick={submitTimesheet}
                          disabled={entries.length === 0 || hasMissingProjects || hasMissingDescriptions || hasMissingRoles || isSaving || isSubmitting}
                          className="flex-1 sm:flex-none gap-2 font-semibold"
                          data-tour="timesheet-submit"
                        >
                          {isSubmitting ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Send className="h-4 w-4" />
                          )}
                          {isSubmitting ? 'Submitting...' : 'Submit for Approval'}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
              

            </div>
        </div>
      )}

      {/* Mobile FAB (Floating Action Button) - Only visible on mobile */}
      {!isReadOnly && (
        <Button
          onClick={addRow}
          size="lg"
          className="fixed bottom-20 right-4 z-30 h-14 w-14 rounded-full shadow-2xl md:hidden hover:scale-110 transition-transform"
          title="Add Row"
        >
          <Plus className="h-6 w-6" />
        </Button>
      )}

      {/* Audit History Dialog */}
      <AuditHistoryDialog
        open={showAuditHistory}
        onOpenChange={setShowAuditHistory}
        timesheetId={currentTimesheet.id}
        employeeName={currentEmployee.name}
        weekEnding={currentTimesheet.weekEnding}
        timesheet={currentTimesheet}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmation.open} onOpenChange={(open) => setDeleteConfirmation({ open, index: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Row</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this timesheet entry? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmation({ open: false, index: null })}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>



      {/* Fill Empty Descriptions Dialog — hidden in daily mode */}
      {!isDaily && <Dialog open={showFillDescriptionDialog} onOpenChange={setShowFillDescriptionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Fill Empty Descriptions</DialogTitle>
            <DialogDescription>
              This will update {emptyDescriptionCount} row{emptyDescriptionCount !== 1 ? 's' : ''} with blank descriptions.
              Rows that already have descriptions will not be changed.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              value={bulkDescription}
              onChange={(e) => setBulkDescription(e.target.value)}
              placeholder="Enter description for selected rows..."
              className="min-h-[100px]"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowFillDescriptionDialog(false);
                setBulkDescription('');
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleFillEmptyDescriptions}>
              Fill {emptyDescriptionCount} Description{emptyDescriptionCount !== 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>}

      {/* Booking Details Modal */}
      {selectedBookingForDetails && (
        <BookingDetailsModal
          open={!!selectedBookingForDetails}
          onOpenChange={(open) => !open && setSelectedBookingForDetails(null)}
          booking={{
            projectName: allAvailableProjects.find(p => p.id === selectedBookingForDetails.projectId)?.name || 'Unknown Project',
            costCentreName: costCentres.find(cc => cc.id === selectedBookingForDetails.costCentreId)?.name || 'Unknown Cost Centre',
            scheduledHours: selectedBookingForDetails.scheduledHours || {
              monday: 0,
              tuesday: 0,
              wednesday: 0,
              thursday: 0,
              friday: 0,
              saturday: 0,
              sunday: 0,
            },
            bookingCreatedByName: selectedBookingForDetails.bookingCreatedByName,
            bookingCreatedAt: selectedBookingForDetails.bookingCreatedAt,
            bookingNotes: selectedBookingForDetails.bookingNotes,
          }}
          onDismiss={() => {
            const index = entries.findIndex(e => e.id === selectedBookingForDetails.id);
            if (index !== -1) {
              setSelectedBookingForDismiss(selectedBookingForDetails);
              setSelectedBookingForDetails(null);
            }
          }}
          readOnly={isReadOnly}
        />
      )}

      {/* Dismiss Booking Modal */}
      {selectedBookingForDismiss && (
        <DismissBookingModal
          open={!!selectedBookingForDismiss}
          onOpenChange={(open) => !open && setSelectedBookingForDismiss(null)}
          booking={{
            projectName: allAvailableProjects.find(p => p.id === selectedBookingForDismiss.projectId)?.name || 'Unknown Project',
            scheduledHours: selectedBookingForDismiss.scheduledHours || {
              monday: 0,
              tuesday: 0,
              wednesday: 0,
              thursday: 0,
              friday: 0,
              saturday: 0,
              sunday: 0,
            },
          }}
          onConfirm={(reason, customReason) => {
            const index = entries.findIndex(e => e.id === selectedBookingForDismiss.id);
            if (index !== -1) {
              handleDismissBooking(index, reason, customReason);
            }
            setSelectedBookingForDismiss(null);
          }}
        />
      )}

      {/* Absence Details Modal */}
      {selectedAbsenceForDetails && (
        <AbsenceDetailsModal
          open={!!selectedAbsenceForDetails}
          onOpenChange={(open) => !open && setSelectedAbsenceForDetails(null)}
          entry={{
            absenceType: selectedAbsenceForDetails.absenceType || 'annual-leave',
            absenceReason: selectedAbsenceForDetails.absenceReason,
            approvedBy: selectedAbsenceForDetails.approvedBy,
            approvedAt: selectedAbsenceForDetails.approvedAt,
            absenceId: selectedAbsenceForDetails.absenceId,
            hours: selectedAbsenceForDetails.hours,
            sourceIntegration: selectedAbsenceForDetails.sourceIntegration,
          }}
        />
      )}

      {/* AI Insights Sheet */}
      <AIInsightsSheet
        open={showAIInsightsSheet}
        onOpenChange={setShowAIInsightsSheet}
        warnings={aiWarnings}
        onDismiss={handleDismissWarning}
        onAutoFix={undefined}
        onClassifyOvertime={overtimeEnabled ? handleAIClassifyOvertime : undefined}
        nonDismissableIds={nonDismissableWarningIds}
      />

      {/* Overtime Classification Modal (task 2.17) */}
      {classifyOvertimeEntry && overtimeEnabled && (
        <OvertimeClassificationModal
          open={!!classifyOvertimeEntry}
          onOpenChange={(open) => {
            if (!open) {
              setClassifyOvertimeEntry(null);
              setClassifyOvertimeSuggestedHours({});
            }
          }}
          entry={classifyOvertimeEntry}
          visibleDays={DAYS}
          dayLabels={DAY_LABELS}
          overtimeTypes={activeOvertimeTypes}
          defaultOvertimeTypeId={defaultOvertimeTypeId}
          suggestedOvertimeHours={Object.keys(classifyOvertimeSuggestedHours).length > 0 ? classifyOvertimeSuggestedHours : undefined}
          onConfirm={handleClassifyOvertimeConfirm}
          nextEntryId={-Date.now()}
          projects={allAvailableProjects}
          costCentres={costCentres}
          onServerSearch={handleProjectSearch}
          isSearchingProjects={isSearchingProjects}
          companyId={currentCompany?.id}
        />
      )}

      {/* Pre-stage rule confirmation dialog (Accept / Decline) */}
      <PreStageRuleConfirmationDialog
        open={confirmationDialogOpen}
        message={confirmationMessage}
        isSubmitting={isSubmitting}
        onAccept={handleConfirmationAccept}
        onDecline={handleConfirmationDecline}
      />
      </div>
    </TooltipProvider>
  );
}
